// Data Service Layer - Abstracts between Live, Demo, and Tour modes
import type { FeatureMode, DataMode } from '@/types';
import type { PaginationParams, PaginatedResponse } from '@/types/pagination';
import { paginateArray } from '@/types/pagination';
import * as mockData from './mock-data';
import * as tourMockData from './tour-mock-data';
import { getBudgetDataByProjectId as getMockBudgetData } from './mock-data/budget-mock-data';
import { mockGlobalCategories, mockGlobalSubcategories, mockCategoryStats, getGlobalSubcategoriesByCategory as getMockGlobalSubcategoriesByCategory } from './mock-data/category-mock-data';
import { apiFetch } from './api-error-handler';
import { authFetch, getAuthConfig } from './auth-fetch';
import type { ProjectBudgetData } from '@/types/budget.types';
import type { GlobalCategory, GlobalSubcategory, CategoryStats } from '@/types/category.types';

// Default API base URL - uses environment variable or defaults to /api for same-origin requests
const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Runtime override for API base URL (used by app-prod to inject PidgetSettings.apiUrl)
let apiBaseUrlOverride: string | null = null;

/**
 * Set the API base URL override. This is called by app-prod during initialization
 * to use the apiUrl from PidgetSettings instead of the environment variable.
 * 
 * @param url - The API base URL to use (trailing slashes will be trimmed)
 */
export function setApiBaseUrlOverride(url: string): void {
  // Trim trailing slashes to prevent double-slash URLs like "https://api.example.com//projects"
  apiBaseUrlOverride = url.replace(/\/+$/, '');
}

/**
 * Get the current API base URL. Returns the override if set (app-prod),
 * otherwise falls back to the environment variable or default '/api' (app).
 */
export function getApiBaseUrl(): string {
  return apiBaseUrlOverride ?? DEFAULT_API_BASE_URL;
}

// Storage keys
const FEATURE_MODE_KEY = 'access-pm-feature-modes';
const SENSITIVE_DATA_KEY = 'access-pm-sensitive-data-visible';
const DATA_MODE_KEY = 'evo-data-mode';

const DEMO_DATA_INSTANCE_ID = 'inst-eu-002';

function isDemoDataCompany(): boolean {
  const config = getAuthConfig();
  return !config.companyId || config.companyId === DEMO_DATA_INSTANCE_ID;
}

interface FeatureModes {
  [featureId: string]: FeatureMode;
}

// Get global data mode from localStorage
export function getDataMode(): DataMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(DATA_MODE_KEY);
    if (stored === 'demo' || stored === 'live' || stored === 'tour') {
      return stored;
    }
    return 'demo';
  } catch {
    return 'demo';
  }
}

// Get feature mode from localStorage (legacy - kept for backward compatibility)
export function getFeatureMode(featureId: string): FeatureMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    if (!stored) return 'demo';
    
    const modes: FeatureModes = JSON.parse(stored);
    return modes[featureId] || 'demo';
  } catch {
    return 'demo';
  }
}

// Set feature mode
export function setFeatureMode(featureId: string, mode: FeatureMode): void {
  if (typeof window === 'undefined') return;
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    const modes: FeatureModes = stored ? JSON.parse(stored) : {};
    modes[featureId] = mode;
    localStorage.setItem(FEATURE_MODE_KEY, JSON.stringify(modes));
    
    // Trigger storage event for other tabs/components
    window.dispatchEvent(new CustomEvent('featureModeChanged', { 
      detail: { featureId, mode } 
    }));
  } catch (error) {
    console.error('Failed to set feature mode:', error);
  }
}

// Get all feature modes
export function getAllFeatureModes(): FeatureModes {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Sensitive data visibility
export function getSensitiveDataVisible(): boolean {
  if (typeof window === 'undefined') return true;
  
  try {
    const stored = localStorage.getItem(SENSITIVE_DATA_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export function setSensitiveDataVisible(visible: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(SENSITIVE_DATA_KEY, String(visible));
    window.dispatchEvent(new CustomEvent('sensitiveDataVisibilityChanged', { 
      detail: { visible } 
    }));
  } catch (error) {
    console.error('Failed to set sensitive data visibility:', error);
  }
}

// Filter parameters for project list
export interface ProjectFilterParams {
  statusId?: number;
  health?: string;
  search?: string;
  sortBy?: string;
}

// Data fetching functions - route based on global data mode
export async function fetchProjects(
  featureId: string = 'projects',
  paginationParams?: PaginationParams,
  filterParams?: ProjectFilterParams
): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (!isDemoDataCompany()) {
      if (paginationParams) {
        return paginateArray([], paginationParams.page, paginationParams.pageSize);
      }
      return [];
    }
    
    let projects = [...mockData.mockProjects];
    
    // Apply filters in demo mode
    if (filterParams) {
      if (filterParams.statusId != null) {
        projects = projects.filter(p => p.statusId === filterParams.statusId);
      }
      if (filterParams.health && filterParams.health !== 'all') {
        projects = projects.filter(p => p.health === filterParams.health);
      }
      if (filterParams.search) {
        const searchLower = filterParams.search.toLowerCase();
        projects = projects.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.code && p.code.toLowerCase().includes(searchLower)) ||
          (p.clientNames && p.clientNames.toLowerCase().includes(searchLower))
        );
      }
      // Apply sorting
      if (filterParams.sortBy) {
        const healthOrder: Record<string, number> = { red: 0, amber: 1, green: 2 };
        switch (filterParams.sortBy) {
          case 'name':
            projects.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'health':
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
            break;
          case 'value':
            projects.sort((a, b) => b.budgetRevenue - a.budgetRevenue);
            break;
          case 'completion':
            projects.sort((a, b) => a.completionPercent - b.completionPercent);
            break;
          default:
            // Default: health (worst first)
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
        }
      }
    }
    
    // If pagination params provided, return paginated response
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(projects, page, pageSize);
    }
    
    // Otherwise return all projects (backward compatibility)
    return projects;
  } else if (mode === 'tour') {
    // Tour mode - use tour-specific mock data
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let projects = [...tourMockData.tourMockProjects];
    
    // Apply filters in tour mode
    if (filterParams) {
      if (filterParams.statusId != null) {
        projects = projects.filter(p => p.statusId === filterParams.statusId);
      }
      if (filterParams.health && filterParams.health !== 'all') {
        projects = projects.filter(p => p.health === filterParams.health);
      }
      if (filterParams.search) {
        const searchLower = filterParams.search.toLowerCase();
        projects = projects.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.code && p.code.toLowerCase().includes(searchLower)) ||
          (p.clientNames && p.clientNames.toLowerCase().includes(searchLower))
        );
      }
      // Apply sorting
      if (filterParams.sortBy) {
        const healthOrder: Record<string, number> = { red: 0, amber: 1, green: 2 };
        switch (filterParams.sortBy) {
          case 'name':
            projects.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'health':
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
            break;
          case 'value':
            projects.sort((a, b) => b.budgetRevenue - a.budgetRevenue);
            break;
          case 'completion':
            projects.sort((a, b) => a.completionPercent - b.completionPercent);
            break;
          default:
            // Default: health (worst first)
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
        }
      }
    }
    
    // If pagination params provided, return paginated response
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(projects, page, pageSize);
    }
    
    // Otherwise return all tour projects
    return projects;
  } else {
    // Live mode - call the API with pagination and filter params
    const params = new URLSearchParams();
    
    if (paginationParams) {
      params.append('page', paginationParams.page.toString());
      params.append('pageSize', paginationParams.pageSize.toString());
    }
    
    if (filterParams) {
      if (filterParams.statusId != null) {
        params.append('statusId', filterParams.statusId.toString());
      }
      if (filterParams.health && filterParams.health !== 'all') {
        params.append('health', filterParams.health);
      }
      if (filterParams.search) {
        params.append('search', filterParams.search);
      }
      if (filterParams.sortBy) {
        params.append('sortBy', filterParams.sortBy);
      }
    }
    
    let url = `${getApiBaseUrl()}/projects`;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiFetch<any>(url);
    
    // Check if response is a paginated response (has data and pagination properties)
    if (paginationParams && response && typeof response === 'object' && 'data' in response && 'pagination' in response) {
      // Server returned paginated response - return it directly
      return {
        data: response.data,
        pagination: response.pagination
      };
    }
    
    // Validate that the response is an array (non-paginated response)
    // The API should return a JSON array, but if it returns an object or other type,
    // we need to handle it gracefully to prevent ".filter is not a function" errors
    let projects: any[];
    if (Array.isArray(response)) {
      projects = response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        projects = response.items;
      } else if (Array.isArray(response.data)) {
        projects = response.data;
      } else if (Array.isArray(response.value)) {
        projects = response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        projects = response.$values;
      } else {
        console.error('API returned unexpected format for projects:', response);
        throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
      }
    } else {
      console.error('API returned unexpected format for projects:', response);
      throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
    }
    
    return projects;
  }
}

export async function fetchProjectById(projectId: number): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return mockData.mockProjects.find(p => p.id === projectId) || null;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return tourMockData.tourMockProjects.find(p => p.id === projectId) || null;
  } else {
    return apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`);
  }
}

export interface UpdateProjectData {
  id: number;
  name?: string;
  description?: string;
  statusId?: number;
  type?: string;
  revenueRecognitionMethod?: string;
  startDate?: string;
  endDate?: string;
  clientIds?: string;
  projectManagerId?: number;
  health?: string;
}

export async function updateProject(id: number, data: UpdateProjectData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      ...data,
      updatedAt: new Date().toISOString(),
    };
  } else {
    if (!id) {
      throw new Error('Project ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Project ID mismatch.');
    }
    
    const projectPayload = {
      id: data.id,
      name: data.name,
      description: data.description || null,
      statusId: data.statusId,
      type: data.type,
      revenueRecognitionMethod: data.revenueRecognitionMethod || null,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      clientIds: data.clientIds || null,
      projectManagerId: data.projectManagerId || null,
      health: data.health || null,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/projects/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectPayload),
    });
  }
}

export async function fetchClients(featureId: string = 'clients') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockClients;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return tourMockData.tourMockClients;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/clients`);
  }
}

// Role type for the Role table
export interface RoleOption {
  id: number;
  name: string;
  description?: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate?: number;
  defaultDailyBillRate?: number;
}

export async function fetchRoles(): Promise<RoleOption[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, name: 'Project Manager', defaultCostRate: 100, defaultBillRate: 200 },
      { id: 2, name: 'Senior Consultant', defaultCostRate: 90, defaultBillRate: 180 },
      { id: 3, name: 'Consultant', defaultCostRate: 75, defaultBillRate: 150 },
      { id: 4, name: 'Developer', defaultCostRate: 75, defaultBillRate: 150 },
      { id: 5, name: 'Senior Developer', defaultCostRate: 88, defaultBillRate: 175 },
      { id: 6, name: 'QA Analyst', defaultCostRate: 63, defaultBillRate: 125 },
      { id: 7, name: 'QA Engineer', defaultCostRate: 63, defaultBillRate: 125 },
      { id: 8, name: 'Business Analyst', defaultCostRate: 70, defaultBillRate: 140 },
      { id: 9, name: 'Technical Architect', defaultCostRate: 110, defaultBillRate: 220 },
      { id: 10, name: 'UX Designer', defaultCostRate: 80, defaultBillRate: 160 },
      { id: 11, name: 'Scrum Master', defaultCostRate: 88, defaultBillRate: 175 },
    ];
  } else {
    const roles = await apiFetch<RoleOption[]>(`${getApiBaseUrl()}/roles`);
    return roles;
  }
}
export interface RoleWithRates {
  id: number;
  name: string;
  description?: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate?: number;
  defaultDailyBillRate?: number;
}

export async function fetchRolesWithRates(): Promise<RoleWithRates[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, name: 'Project Manager', description: 'Experienced project manager overseeing project delivery and team coordination', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120 },
      { id: 2, name: 'Senior Consultant', description: 'Senior consultant providing strategic guidance and expertise', defaultCostRate: 90, defaultBillRate: 180, defaultDailyCostRate: 720, defaultDailyBillRate: 1440 },
      { id: 3, name: 'Consultant', description: 'Consultant delivering solutions and client engagement', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200 },
      { id: 4, name: 'Developer', description: 'Software developer building and maintaining applications', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200 },
      { id: 5, name: 'Senior Developer', description: 'Experienced software developer with 5+ years of expertise in full-stack development', defaultCostRate: 88, defaultBillRate: 175, defaultDailyCostRate: 704, defaultDailyBillRate: 1400 },
      { id: 6, name: 'QA Analyst', description: 'Quality assurance analyst ensuring software quality through testing', defaultCostRate: 63, defaultBillRate: 125, defaultDailyCostRate: 504, defaultDailyBillRate: 1000 },
      { id: 7, name: 'QA Engineer', description: 'Quality assurance specialist ensuring software quality through testing and automation', defaultCostRate: 63, defaultBillRate: 125, defaultDailyCostRate: 504, defaultDailyBillRate: 1000 },
      { id: 8, name: 'Business Analyst', description: 'Analyst bridging business needs with technical solutions and requirements', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120 },
      { id: 9, name: 'Technical Architect', description: 'Technical architect designing system architecture and infrastructure', defaultCostRate: 110, defaultBillRate: 220, defaultDailyCostRate: 880, defaultDailyBillRate: 1760 },
      { id: 10, name: 'UX Designer', description: 'Creative designer specializing in user experience and interface design', defaultCostRate: 80, defaultBillRate: 160, defaultDailyCostRate: 640, defaultDailyBillRate: 1280 },
      { id: 11, name: 'Scrum Master', description: 'Scrum master facilitating agile processes and team collaboration', defaultCostRate: 88, defaultBillRate: 175, defaultDailyCostRate: 704, defaultDailyBillRate: 1400 },
    ];
  } else {
    return apiFetch<RoleWithRates[]>(`${getApiBaseUrl()}/roles`);
  }
}

export interface RoleEmployee {
  id: number;
  name: string;
  email: string;
  department: string;
  photo?: string;
}

export async function fetchEmployeesByRoleId(roleId: number): Promise<RoleEmployee[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    return apiFetch<RoleEmployee[]>(`${getApiBaseUrl()}/roles/${roleId}/employees`);
  }
}

export async function fetchEmployees(featureId: string = 'employees') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    // Use all mock employees from mock-data.ts
    return mockData.mockEmployees;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return tourMockData.tourMockEmployees;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/employees`);
  }
}

export async function fetchCompanies(featureId: string = 'companies') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockCompanies.filter(c => c.status === 'active');
  } else {
    // In live mode, fetch active companies from API
    return apiFetch<any[]>(`${getApiBaseUrl()}/companies/active`);
  }
}

export async function fetchAllCompanies() {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockCompanies;
  } else {
    // In live mode, fetch all companies from API
    return apiFetch<any[]>(`${getApiBaseUrl()}/companies`);
  }
}

export interface CreateCompanyData {
  name: string;
  code: string;
  instanceId?: string;
  status?: string;
  description?: string;
  employeeCount?: number;
  activeProjects?: number;
}

export async function createCompany(data: CreateCompanyData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const mockCompany = {
      id: `demo-company-${Date.now()}`,
      name: data.name,
      code: data.code,
      instanceId: data.instanceId || '',
      status: data.status || 'setup',
      description: data.description || '',
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return mockCompany;
  } else {
    const companyPayload = {
      name: data.name,
      code: data.code,
      ...(data.instanceId ? { instanceId: data.instanceId } : {}),
      status: data.status || 'setup',
      ...(data.description ? { description: data.description } : {}),
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/companies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyPayload),
    });
  }
}

export interface UpdateCompanyData {
  id: string;
  name: string;
  code: string;
  instanceId?: string;
  status?: string;
  description?: string;
  employeeCount?: number;
  activeProjects?: number;
}

export async function updateCompany(id: string, data: UpdateCompanyData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      ...data,
      updatedAt: new Date(),
    };
  } else {
    if (!id) {
      throw new Error('Company ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Company ID mismatch.');
    }
    
    const companyPayload = {
      id: data.id,
      name: data.name,
      code: data.code,
      instanceId: data.instanceId || null,
      status: data.status || 'setup',
      description: data.description || null,
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/companies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyPayload),
    });
  }
}

export async function deleteCompany(id: string): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    if (!id) {
      throw new Error('Company ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/companies/${id}`, {
      method: 'DELETE',
    });
  }
}

export async function fetchResourceAllocations(featureId: string = 'resources') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 250));
    // Use enhanced mock allocations for resource assignment demo
    const { mockExistingAllocations } = await import('./mock-allocation-data');
    return mockExistingAllocations;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/resourceallocations`);
  }
}

export interface CreateResourceAllocationData {
  projectId: number;
  employeeId: number;
  role: string;
  allocatedHours?: number;
  allocationPercent?: number;
  startDate: string;
  endDate: string;
  status?: 'confirmed' | 'tentative';
}

export async function createResourceAllocation(data: CreateResourceAllocationData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const mockAllocation = {
      id: `demo-allocation-${Date.now()}`,
      ...data,
      status: data.status || 'confirmed',
      conflictLevel: 'none',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    return mockAllocation;
  } else {
    const allocationPayload = {
      projectId: data.projectId,
      employeeId: data.employeeId,
      role: data.role,
      allocationPercent: data.allocationPercent || 100,
      allocatedHours: data.allocatedHours || null,
      startDate: data.startDate,
      endDate: data.endDate,
      status: data.status || 'confirmed',
      conflictLevel: 'none',
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/resourceallocations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(allocationPayload),
    });
  }
}

export async function deleteResourceAllocation(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  } else {
    if (!id) {
      throw new Error('Resource allocation ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/resourceallocations/${id}`, {
      method: 'DELETE',
    });
  }
}

export async function fetchWIPReports(featureId: string = 'wip-reporting') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    if (!isDemoDataCompany()) return [];
    return mockData.mockWIPReports;
  } else {
    // WIP Reports are computed from project data - fetch projects and compute WIP
    const projects = await apiFetch<any[]>(`${getApiBaseUrl()}/projects`);
    
    // Transform projects into WIP report format
    // Now uses clientNames from the project (populated by stored procedure)
    return projects.map(project => {
      return {
        projectId: project.id,
        projectName: project.name,
        clientName: project.clientNames || 'Unknown',
        costsIncurred: project.actualLabour + project.actualExpense,
        profitEarned: project.actualRevenue - (project.actualLabour + project.actualExpense),
        amountBilled: project.actualRevenue,
        wipAmount: project.wipAmount,
        agingDays: Math.floor((new Date().getTime() - new Date(project.startDate).getTime()) / (1000 * 60 * 60 * 24)),
        readyToBill: project.wipAmount > 0,
        overBilling: project.wipAmount < 0 ? Math.abs(project.wipAmount) : 0,
        underBilling: project.wipAmount > 0 ? project.wipAmount : 0,
      };
    });
  }
}

export async function fetchUtilisationData(featureId: string = 'utilisation') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    if (!isDemoDataCompany()) return [];
    return mockData.mockUtilisationData;
  } else {
    // Utilisation data is computed from employees and time entries
    const employees = await apiFetch<any[]>(`${getApiBaseUrl()}/employees`);
    const timeEntries = await apiFetch<any[]>(`${getApiBaseUrl()}/timeentries`);
    
    // Calculate utilisation for each employee
    const standardHoursPerMonth = 160; // Assuming 40 hours/week * 4 weeks
    
    return employees.map(employee => {
      const employeeTimeEntries = timeEntries.filter(te => te.employeeId === employee.id);
      const billableHours = employeeTimeEntries
        .filter(te => te.billable)
        .reduce((sum, te) => sum + te.hours, 0);
      const totalHours = employeeTimeEntries.reduce((sum, te) => sum + te.hours, 0);
      const utilisationPercent = standardHoursPerMonth > 0 
        ? (billableHours / standardHoursPerMonth) * 100 
        : 0;
      const targetPercent = 75; // Default target
      
      // Determine status based on utilisation vs target
      const status: 'under' | 'target' | 'over' = 
        utilisationPercent < targetPercent - 10 ? 'under' : 
        utilisationPercent > targetPercent + 10 ? 'over' : 'target';
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        role: employee.role,
        department: employee.department,
        billableHours,
        availableHours: standardHoursPerMonth,
        utilisationPercent,
        targetPercent,
        status,
      };
    });
  }
}

export async function fetchPortfolioSummary(featureId: string = 'portfolio') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 350));
    if (!isDemoDataCompany()) {
      return {
        totalProjects: 0,
        activeProjects: 0,
        portfolioValue: 0,
        totalWIP: 0,
        avgMarginPercent: 0,
        avgUtilisationPercent: 0,
        projectsByStatus: { 'not-started': 0, 'in-progress': 0, 'on-hold': 0, 'completed': 0, 'cancelled': 0 },
        projectsByHealth: { 'green': 0, 'amber': 0, 'red': 0 },
      };
    }
    return mockData.mockPortfolioSummary;
  } else {
    const summary = await apiFetch<any>(`${getApiBaseUrl()}/projects/summary`);
    
    return {
      totalProjects: summary.totalProjects ?? 0,
      activeProjects: summary.activeProjects ?? 0,
      portfolioValue: summary.portfolioValue ?? 0,
      totalWIP: summary.totalWIP ?? 0,
      avgMarginPercent: summary.avgMarginPercent ?? 0,
      avgUtilisationPercent: 75,
      projectsByStatus: {
        'not-started': summary.statusNotStarted ?? 0,
        'in-progress': summary.statusInProgress ?? 0,
        'on-hold': summary.statusOnHold ?? 0,
        'completed': summary.statusCompleted ?? 0,
        'cancelled': summary.statusCancelled ?? 0,
      },
      projectsByHealth: {
        'green': summary.healthGreen ?? 0,
        'amber': summary.healthAmber ?? 0,
        'red': summary.healthRed ?? 0,
      },
    };
  }
}

export async function fetchIntegrationStatus(featureId: string = 'integrations') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return mockData.mockIntegrationStatus;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/integrationstatus`);
  }
}

export async function fetchTasks(featureId: string = 'tasks') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockTasks;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/tasks`);
  }
}

export async function fetchMilestones(featureId: string = 'milestones') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockMilestones;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/milestones`);
  }
}

export async function fetchMilestonesByProjectId(projectId: number, featureId: string = 'milestones') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockMilestones.filter(m => m.projectId === projectId);
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/milestones/by-project/${projectId}`);
  }
}

export async function fetchTimeEntriesByProjectId(projectId: number, featureId: string = 'timeentries') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockTimeEntries.filter(te => te.projectId === projectId);
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/timeentries/by-project/${projectId}`);
  }
}

export interface FetchExpensesOptions {
  includeApprovedExpenses?: boolean;
  includeCreditCardExpenses?: boolean;
}

export async function fetchExpensesByProjectId(
  projectId: number,
  projectCode?: string,
  company?: { instanceId?: string; expenseSubscriptionId?: string; expenseUserEmail?: string; expenseAccessToken?: string } | null,
  featureId: string = 'expenses',
  options?: FetchExpensesOptions
) {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockExpenses.filter(e => e.projectId === projectId);
  } else {
    const { fetchApprovedExpenses, fetchApprovedCreditCardExpenses } = await import('./expense-api');
    
    const includeApproved = options?.includeApprovedExpenses ?? true;
    const includeCreditCard = options?.includeCreditCardExpenses ?? false;
    
    const fetches: Promise<any[]>[] = [];
    
    if (includeApproved) {
      fetches.push(fetchApprovedExpenses(projectId, projectCode, company));
    }
    if (includeCreditCard) {
      fetches.push(fetchApprovedCreditCardExpenses(projectId, projectCode, company));
    }
    
    if (fetches.length === 0) {
      return [];
    }
    
    const results = await Promise.all(fetches);
    const combined = results.flat();
    
    combined.forEach((expense, index) => {
      expense.id = index + 1;
    });
    
    return combined;
  }
}

export async function fetchChangeRequestsByProjectId(projectId: number, featureId: string = 'changerequests') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    // No mock change requests in mock-data.ts, return empty array for demo mode
    return [];
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/changerequests/by-project/${projectId}`);
  }
}

export async function fetchCostCentres(featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    const response = await apiFetch<any>(`${getApiBaseUrl()}/costcentres`);
    
    // Handle different response formats from the API
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        return response.items;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response.value)) {
        return response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        return response.$values;
      }
    }
    
    console.warn('fetchCostCentres: Unexpected response format:', response);
    return [];
  }
}

export async function fetchCostCentresByProjectId(projectId: number, featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    const response = await apiFetch<any>(`${getApiBaseUrl()}/costcentres/by-project/${projectId}`);
    
    // Handle different response formats from the API
    // The API should return a JSON array, but may return wrapped formats
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        return response.items;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response.value)) {
        return response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        return response.$values;
      }
    }
    
    // If we can't extract an array, log a warning and return empty array
    console.warn('fetchCostCentresByProjectId: Unexpected response format:', response);
    return [];
  }
}

// Fetch all global categories
export async function fetchGlobalCategories(featureId: string = 'globalcategories'): Promise<GlobalCategory[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockGlobalCategories;
  } else {
    return apiFetch<GlobalCategory[]>(`${getApiBaseUrl()}/globalcategories`);
  }
}

// Create a new global category
export interface CreateGlobalCategoryData {
  name: string;
  code?: string;
  type: string;
  description?: string;
  isDefault: boolean;
}

export async function createGlobalCategory(data: CreateGlobalCategoryData): Promise<GlobalCategory> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newCategory: GlobalCategory = {
      id: Date.now(),
      name: data.name,
      code: data.code,
      type: data.type as GlobalCategory['type'],
      description: data.description,
      status: 'active',
      isDefault: data.isDefault,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'Current User',
      usageCount: 0,
    };
    
    return newCategory;
  } else {
    const payload = {
      name: data.name,
      code: data.code || null,
      type: data.type,
      description: data.description || null,
      status: 'active',
      isDefault: data.isDefault,
      createdBy: 'Current User',
      usageCount: 0,
    };
    
    return apiFetch<GlobalCategory>(`${getApiBaseUrl()}/globalcategories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }
}

// Update an existing global category
export interface UpdateGlobalCategoryData {
  id: number;
  name: string;
  code?: string;
  type: string;
  description?: string;
  status: string;
  isDefault: boolean;
  usageCount: number;
}

export async function updateGlobalCategory(id: number, data: UpdateGlobalCategoryData): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    if (id !== data.id) {
      throw new Error('Category ID mismatch.');
    }
    
    const payload = {
      id: data.id,
      name: data.name,
      code: data.code || null,
      type: data.type,
      description: data.description || null,
      status: data.status,
      isDefault: data.isDefault,
      usageCount: data.usageCount,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/globalcategories/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }
}

// Delete a global category
export async function deleteGlobalCategory(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/globalcategories/${id}`, {
      method: 'DELETE',
    });
  }
}

// Fetch all global subcategories
export async function fetchGlobalSubcategories(featureId: string = 'globalsubcategories'): Promise<GlobalSubcategory[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockGlobalSubcategories;
  } else {
    return apiFetch<GlobalSubcategory[]>(`${getApiBaseUrl()}/globalsubcategories`);
  }
}

// Fetch global subcategories by category ID
export async function fetchGlobalSubcategoriesByCategory(categoryId: number, featureId: string = 'globalsubcategories'): Promise<GlobalSubcategory[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return getMockGlobalSubcategoriesByCategory(categoryId);
  } else {
    return apiFetch<GlobalSubcategory[]>(`${getApiBaseUrl()}/globalsubcategories/by-category/${categoryId}`);
  }
}

// Fetch default GlobalCategories and convert them to CostCategory format for auto-populating new cost budgets
export async function fetchDefaultCategoriesAsCostCategories(): Promise<import('@/types/budget.types').CostCategory[]> {
  const globalCategories = await fetchGlobalCategories();
  return globalCategories
    .filter(gc => gc.isDefault && gc.status === 'active')
    .map(gc => ({
      id: gc.id,
      name: gc.name,
      type: gc.type,
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'global' as const,
      globalCategoryId: gc.id,
      isCustom: false,
    }));
}

// Cost centre transaction interface
export interface CostCentreTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
}

// Cost centre breakdown interface
export interface CostCentreBreakdown {
  labour: number;
  expenses: number;
  materials: number;
  subcontractors: number;
}

// Fetch cost centre transactions (expenses and time entries)
export async function fetchCostCentreTransactions(
  costCentreId: number,
  projectId: number
): Promise<CostCentreTransaction[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return mock transactions
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: '1', date: '2024-01-15', description: 'Developer salary - January', amount: 5000, category: 'Labour' },
      { id: '2', date: '2024-01-14', description: 'Software licenses', amount: 1200, category: 'Expenses' },
      { id: '3', date: '2024-01-12', description: 'Cloud hosting services', amount: 800, category: 'Infrastructure' },
      { id: '4', date: '2024-01-10', description: 'Design tools subscription', amount: 450, category: 'Expenses' },
      { id: '5', date: '2024-01-08', description: 'Contractor payment', amount: 3500, category: 'Subcontractors' },
      { id: '6', date: '2024-01-05', description: 'Office supplies', amount: 250, category: 'Materials' },
      { id: '7', date: '2024-01-03', description: 'Training materials', amount: 600, category: 'Expenses' },
      { id: '8', date: '2024-01-02', description: 'Equipment purchase', amount: 2100, category: 'Materials' },
      { id: '9', date: '2023-12-28', description: 'Consultant fees', amount: 4200, category: 'Subcontractors' },
      { id: '10', date: '2023-12-20', description: 'Team building event', amount: 850, category: 'Expenses' },
    ];
  } else {
    // In live mode, fetch expenses from the project and return as transactions
    try {
      const expenses = await apiFetch<any[]>(`${getApiBaseUrl()}/expenses/by-project/${projectId}`);
      
      // Transform expenses to transaction format and sort by date descending
      const transactions: CostCentreTransaction[] = expenses
        .map(expense => ({
          id: expense.id,
          date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : '',
          description: expense.description || expense.category || 'Expense',
          amount: expense.amount || 0,
          category: expense.category || 'Expenses',
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10); // Return only the 10 most recent
      
      return transactions;
    } catch (error) {
      console.warn(`[fetchCostCentreTransactions] Failed to fetch transactions for cost centre ${costCentreId}:`, error);
      return [];
    }
  }
}

// Fetch cost centre breakdown (aggregated by category)
export async function fetchCostCentreBreakdown(
  costCentreId: number,
  projectId: number,
  actualCosts: number
): Promise<CostCentreBreakdown> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return mock breakdown based on percentages
    await new Promise(resolve => setTimeout(resolve, 200));
    return {
      labour: actualCosts * 0.6,
      expenses: actualCosts * 0.15,
      materials: actualCosts * 0.15,
      subcontractors: actualCosts * 0.1,
    };
  } else {
    // In live mode, fetch expenses and time entries to calculate breakdown
    try {
      const [expenses, timeEntries] = await Promise.all([
        apiFetch<any[]>(`${getApiBaseUrl()}/expenses/by-project/${projectId}`),
        apiFetch<any[]>(`${getApiBaseUrl()}/timeentries/by-project/${projectId}`),
      ]);
      
      // Calculate labour costs from time entries
      const labourCosts = timeEntries.reduce((sum, entry) => {
        const cost = (entry.hours || 0) * (entry.costRate || 0);
        return sum + cost;
      }, 0);
      
      // Aggregate expenses by category
      const expensesByCategory = expenses.reduce((acc, expense) => {
        const category = (expense.category || 'Expenses').toLowerCase();
        const amount = expense.amount || 0;
        
        if (category.includes('material') || category.includes('equipment')) {
          acc.materials += amount;
        } else if (category.includes('subcontract') || category.includes('contractor') || category.includes('consultant')) {
          acc.subcontractors += amount;
        } else {
          acc.expenses += amount;
        }
        
        return acc;
      }, { materials: 0, subcontractors: 0, expenses: 0 });
      
      return {
        labour: labourCosts,
        expenses: expensesByCategory.expenses,
        materials: expensesByCategory.materials,
        subcontractors: expensesByCategory.subcontractors,
      };
    } catch (error) {
      console.warn(`[fetchCostCentreBreakdown] Failed to fetch breakdown for cost centre ${costCentreId}:`, error);
      // Return zeros if API fails
      return {
        labour: 0,
        expenses: 0,
        materials: 0,
        subcontractors: 0,
      };
    }
  }
}

// Create a new cost centre
export interface CreateCostCentreData {
  projectId: number;
  code: string;
  name: string;
  type?: string;
  status?: string;
  billable?: boolean;
  owner?: string;
  description?: string;
  costsBudget?: number;
  revenueBudget?: number;
  hoursBudget?: number;
}

export async function createCostCentre(data: CreateCostCentreData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created cost centre with generated ID
    const mockCostCentre = {
      id: `demo-cc-${Date.now()}`,
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return mockCostCentre;
  } else {
    // Live mode - validate required fields before calling API
    if (!data.projectId) {
      throw new Error('Project ID is required to create a cost centre.');
    }
    if (!data.code) {
      throw new Error('Cost centre code is required.');
    }
    if (!data.name) {
      throw new Error('Cost centre name is required.');
    }
    
    // Build the payload matching the backend CostCentre entity
    const costCentrePayload = {
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/costcentres`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costCentrePayload),
    });
  }
}

// Update cost centre data interface
export interface UpdateCostCentreData {
  id: number;
  projectId: number;
  code: string;
  name: string;
  type?: string;
  status?: string;
  billable?: boolean;
  owner?: string;
  description?: string;
  costsBudget?: number;
  actualCosts?: number;
  committedCosts?: number;
  revenueBudget?: number;
  actualRevenue?: number;
  hoursBudget?: number;
  actualHours?: number;
  isActive?: boolean;
}

// Update an existing cost centre
export async function updateCostCentre(id: number, data: UpdateCostCentreData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate update but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return the updated cost centre
    return {
      ...data,
      lastUpdated: new Date().toISOString(),
    };
  } else {
    // Live mode - validate required fields before calling API
    if (!id) {
      throw new Error('Cost centre ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Cost centre ID mismatch.');
    }
    
    // Build the payload matching the backend CostCentre entity
    const costCentrePayload = {
      id: data.id,
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || '',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: data.actualCosts || 0,
      committedCosts: data.committedCosts || 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: data.actualRevenue || 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: data.actualHours || 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/costcentres/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costCentrePayload),
    });
  }
}

// Delete a cost centre
export async function deleteCostCentre(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate deletion but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    // Live mode - validate required fields before calling API
    if (!id) {
      throw new Error('Cost centre ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/costcentres/${id}`, {
      method: 'DELETE',
    });
  }
}

// Check if a project code already exists (for uniqueness validation)
export async function checkProjectCodeExists(code: string): Promise<boolean> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    // In demo/tour mode, simulate the check against mock data
    await new Promise(resolve => setTimeout(resolve, 200));
    const projects = mode === 'demo' ? mockData.mockProjects : tourMockData.tourMockProjects;
    return projects.some(p => p.code?.toLowerCase() === code.toLowerCase());
  } else {
    // Live mode - call the API
    const response = await apiFetch<{ exists: boolean }>(`${getApiBaseUrl()}/projects/check-code-exists?code=${encodeURIComponent(code)}`);
    return response.exists;
  }
}

// Create a new project
// Note: Fields are optional here because the form validation (which respects Project Creation Settings)
// is the source of truth for which fields are required
export interface CreateProjectData {
  name: string;
  projectCode?: string;
  description?: string;
  statusId?: number;
  priority?: string;
  type?: string;
  location?: string;
  startDate?: Date;
  endDate?: Date;
  projectOwner?: string;
  projectManager?: string;
  projectManagers?: string[];
  clients?: string[];
  currencyCode?: string;
  budget?: string;
  tags?: string[];
}

export async function createProject(data: CreateProjectData): Promise<any> {
  const mode = getDataMode();
  
  // Extract optional fields with defaults - validation is handled by the form based on Project Creation Settings
  const clientIds = data.clients || [];
  const projectManagerId = data.projectManager || '';
  const additionalProjectManagerIds = data.projectManagers || [];
  const projectOwnerId = data.projectOwner || '';
  const tags = data.tags || [];
  
  // Parse and validate budget value as a string to preserve full DECIMAL(18,2) precision
  // JavaScript's Number type can only safely handle ~15-16 significant digits, but by keeping
  // the value as a string, we let the backend/database handle the full precision
  const parseBudgetString = (budget: string | undefined): string => {
    if (!budget) return '0';
    // Remove any non-numeric characters except decimal point and minus
    const cleaned = budget.replace(/[^0-9.-]+/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.') return '0';
    // Validate it's a proper number format
    const parts = cleaned.split('.');
    if (parts.length > 2) return '0'; // Invalid: multiple decimal points
    // Ensure we have at most 16 digits before decimal and 2 after (DECIMAL(18,2))
    const integerPart = parts[0].replace('-', '');
    const decimalPart = parts[1] || '';
    if (integerPart.length > 16) return '0'; // Exceeds max
    if (decimalPart.length > 2) {
      // Truncate to 2 decimal places
      return parts[0] + '.' + decimalPart.slice(0, 2);
    }
    return cleaned;
  };
  
  const budgetRevenue = parseBudgetString(data.budget);
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created project with generated ID
    const mockProject = {
      id: `demo-${Date.now()}`,
      name: data.name,
      code: data.projectCode || '',
      clientIds: clientIds.join(','),
      clientNames: clientIds.map(id => `Client ${id}`).join(', '),
      projectManagerId: projectManagerId || 'demo-manager',
      additionalProjectManagerIds: additionalProjectManagerIds.join(','),
      projectOwnerId: projectOwnerId,
      type: data.type || '',
      statusId: data.statusId || 1,
      priority: data.priority || '',
      health: 'green',
      startDate: data.startDate?.toISOString() || '',
      endDate: data.endDate?.toISOString() || '',
      description: data.description || '',
      location: data.location || '',
      currencyCode: data.currencyCode || '',
      tags: tags.join(','),
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    return mockProject;
  } else {
    // Live mode - call the API
    // Use conditional spreads to omit optional fields when not provided
    // This prevents sending empty strings for DateTime fields which the API can't parse
    const projectPayload = {
      name: data.name,
      ...(data.projectCode ? { code: data.projectCode } : {}),
      ...(clientIds.length > 0 ? { clientIds: clientIds.join(',') } : {}),
      ...(projectManagerId ? { projectManagerId } : {}),
      ...(additionalProjectManagerIds.length > 0 ? { additionalProjectManagerIds: additionalProjectManagerIds.join(',') } : {}),
      ...(projectOwnerId ? { projectOwnerId } : {}),
      ...(data.type ? { type: data.type } : {}),
      ...(data.statusId ? { statusId: data.statusId } : {}),
      ...(data.priority ? { priority: data.priority } : {}),
      health: 'green',
      ...(data.startDate ? { startDate: data.startDate.toISOString() } : {}),
      ...(data.endDate ? { endDate: data.endDate.toISOString() } : {}),
      ...(data.description ? { description: data.description } : {}),
      ...(data.location ? { location: data.location } : {}),
      ...(data.currencyCode ? { currencyCode: data.currencyCode } : {}),
      ...(tags.length > 0 ? { tags: tags.join(',') } : {}),
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectPayload),
    });
  }
}

// Helper to get project with related data
export async function fetchProjectDetails(projectId: number, featureId: string = 'projects') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const project = mockData.getProjectById(projectId);
    if (!project) return null;
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean).map((id: string) => parseInt(id, 10)) || [];
    const projectClients = clientIds.map(id => mockData.getClientById(id)).filter(Boolean);
    const manager = mockData.getEmployeeById(project.projectManagerId);
    const allocations = mockData.getAllocationsByProject(projectId);
    const team = allocations.map(a => mockData.getEmployeeById(a.employeeId)).filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  } else {
    // Fetch project details from API
    const project = await apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`);
    if (!project) return null;
    
    // Fetch related data in parallel
    const [clients, employees, allocations] = await Promise.all([
      apiFetch<any[]>(`${getApiBaseUrl()}/clients`),
      apiFetch<any[]>(`${getApiBaseUrl()}/employees`),
      apiFetch<any[]>(`${getApiBaseUrl()}/resourceallocations/by-project/${projectId}`),
    ]);
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean) || [];
    const projectClients = clientIds.map((id: string) => clients.find(c => c.id === id)).filter(Boolean);
    const manager = employees.find(e => e.id === project.projectManagerId);
    const team = allocations
      .map(a => employees.find(e => e.id === a.employeeId))
      .filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  }
}

// Backend entity types for budget data
interface BackendCostBudget {
  id: string;
  projectId: number;
  totalBudget: number;
  totalActual: number;
  totalCommitted: number;
  totalAvailable: number;
  progressPercent: number;
}

interface BackendCostCategory {
  id: string;
  costBudgetId: string;
  name: string;
  type: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  scope?: string;
  globalCategoryId?: string;
  isCustom: boolean;
  monthlyBudgets?: string; // JSON string: {"2024-12": 1000, "2025-01": 500, ...}
}

interface BackendSalesCategory {
  id: string;
  salesBudgetId: string;
  name: string;
  type: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  scope?: string;
  globalCategoryId?: string;
  isCustom: boolean;
  monthlyBudgets?: string;
}

interface BackendSalesBudget {
  id: string;
  projectId: number;
  projectType: string;
  contractValue?: number;
  invoiced?: number;
  recognised?: number;
  remaining?: number;
  estimated?: number;
  actual?: number;
  nteAmount?: number;
  unbilledTime?: number;
  unbilledExpenses?: number;
  estimatedRevenue?: number;
  monthlyRetainer?: number;
  includedHours?: number;
  overageRate?: number;
}

interface BackendBillingMilestone {
  id: string;
  salesBudgetId: string;
  name: string;
  amount: number;
  dueDate: string;
  invoiced: boolean;
  invoiceDate?: string;
  paid: boolean;
  paidDate?: string;
  status: string;
}

interface BackendResourceBudget {
  id: string;
  projectId: number;
  totalBudget: number;
  totalActual: number;
  totalRemaining: number;
  progressPercent: number;
  billablePercent: number;
  targetBillablePercent: number;
  totalBudgetCost: number;
  totalActualCost: number;
}

interface BackendBudgetAlert {
  id: string;
  projectId: number;
  type: string;
  severity: string;
  title: string;
  description: string;
  message: string;
  details?: string;
  category?: string;
  actionLabel?: string;
  timestamp?: string;
  createdAt: string;
  dismissed: boolean;
}

interface BackendBudgetRevision {
  id: string;
  projectId: number;
  revisionNumber: number;
  date: string;
  createdAt?: string;
  createdBy?: string;
  changedBy: string;
  changedByName: string;
  reason: string;
  status: string;
  costBudget: number;
  salesBudget: number;
  resourceBudget: number;
  budgetData?: string;
  costChange?: number;
  salesChange?: number;
  resourceChange?: number;
  approvedBy?: string;
  approvedByName?: string;
  approvedDate?: string;
}

interface BackendRoleBudget {
  id: string;
  resourceBudgetId: string;
  roleId: number | null;
  roleName: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  percentUsed: number;
  billRate: number;
  budgetCost: number;
  actualCost: number;
  monthlyHours?: string; // JSON string of number[] from backend
}

interface BackendPersonBudget {
  id: string;
  resourceBudgetId: string;
  personId: string;
  personName: string;
  roleId?: number;
  roleName?: string;
  budgetHours: number;
  actualHours: number;
  utilisation: number;
  costRate: number;
  budgetCost: number;
  actualCost: number;
}

interface BackendPhaseBudgetResource {
  id: string;
  resourceBudgetId: string;
  phaseId?: string;
  phaseName: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  status: string;
  startDate?: string;
  endDate?: string;
}

// Transform backend budget data to frontend ProjectBudgetData format
function transformBudgetData(
  projectId: number,
  costBudget: BackendCostBudget | null,
  salesBudget: BackendSalesBudget | null,
  resourceBudget: BackendResourceBudget | null,
  alerts: BackendBudgetAlert[],
  revisions: BackendBudgetRevision[],
  roleBudgets: BackendRoleBudget[] = [],
  personBudgets: BackendPersonBudget[] = [],
  phaseBudgetResources: BackendPhaseBudgetResource[] = [],
  costCategories: BackendCostCategory[] = [],
  billingMilestones: BackendBillingMilestone[] = [],
  salesCategories: BackendSalesCategory[] = []
): ProjectBudgetData | null {
  // If no core budget data exists, return null
  if (!costBudget && !salesBudget && !resourceBudget) {
    return null;
  }

  const projectType = (salesBudget?.projectType || 'fixed-price') as import('@/types/budget.types').ProjectType;
  
  // Compute summary from available data
  const revenueBudget = salesBudget?.contractValue ?? salesBudget?.estimated ?? 0;
  const revenueActual = salesBudget?.invoiced ?? salesBudget?.actual ?? 0;
  const costBudgetTotal = costBudget?.totalBudget ?? 0;
  const costActual = costBudget?.totalActual ?? 0;
  const hoursBudget = resourceBudget?.totalBudget ?? 0;
  const hoursActual = resourceBudget?.totalActual ?? 0;
  
  const revenueVariance = revenueActual - revenueBudget;
  const costVariance = costActual - costBudgetTotal;
  const budgetedMargin = revenueBudget > 0 ? ((revenueBudget - costBudgetTotal) / revenueBudget) * 100 : 0;
  const actualMargin = revenueActual > 0 ? ((revenueActual - costActual) / revenueActual) * 100 : 0;
  
  // Determine health based on variances
  const costOverrun = costBudgetTotal > 0 ? (costActual / costBudgetTotal) > 1.1 : false;
  const hoursOverrun = hoursBudget > 0 ? (hoursActual / hoursBudget) > 1.1 : false;
  const health: import('@/types/budget.types').ProjectHealth = costOverrun || hoursOverrun ? 'red' : 
    (costBudget?.progressPercent ?? 0) > 80 || (resourceBudget?.progressPercent ?? 0) > 80 ? 'amber' : 'green';

  const summary: import('@/types/budget.types').BudgetSummary = {
    revenue: {
      budget: revenueBudget,
      actual: revenueActual,
      variance: revenueVariance,
      variancePercent: revenueBudget > 0 ? (revenueVariance / revenueBudget) * 100 : 0,
      progressPercent: revenueBudget > 0 ? (revenueActual / revenueBudget) * 100 : 0,
      trend: revenueVariance >= 0 ? 'up' : 'down',
      status: revenueVariance >= 0 ? 'green' : revenueVariance > -revenueBudget * 0.1 ? 'amber' : 'red',
    },
    costs: {
      budget: costBudgetTotal,
      actual: costActual,
      variance: costVariance,
      variancePercent: costBudgetTotal > 0 ? (costVariance / costBudgetTotal) * 100 : 0,
      progressPercent: costBudget?.progressPercent ?? 0,
      trend: costVariance <= 0 ? 'up' : 'down',
      status: costVariance <= 0 ? 'green' : costVariance < costBudgetTotal * 0.1 ? 'amber' : 'red',
      remaining: costBudget?.totalAvailable ?? 0,
      committed: costBudget?.totalCommitted ?? 0,
      available: costBudget?.totalAvailable ?? 0,
    },
    margin: {
      budgetedMargin,
      actualMargin,
      variance: actualMargin - budgetedMargin,
      status: actualMargin >= budgetedMargin ? 'green' : actualMargin >= budgetedMargin - 5 ? 'amber' : 'red',
    },
    hours: {
      budget: hoursBudget,
      actual: hoursActual,
      remaining: resourceBudget?.totalRemaining ?? 0,
      progressPercent: resourceBudget?.progressPercent ?? 0,
      status: (resourceBudget?.progressPercent ?? 0) <= 80 ? 'green' : (resourceBudget?.progressPercent ?? 0) <= 100 ? 'amber' : 'red',
    },
    health: {
      score: health,
      statusText: health === 'green' ? 'On Track' : health === 'amber' ? 'At Risk' : 'Critical',
      factors: [],
    },
  };

  // Transform cost budget with categories from backend
  const transformedCostBudget: import('@/types/budget.types').CostBudget = {
    projectId,
    categories: costCategories.map(cat => {
      // Parse monthly budgets from JSON string if present
      let monthlyBudgetsData: Record<string, number> | undefined;
      if (cat.monthlyBudgets) {
        try {
          monthlyBudgetsData = JSON.parse(cat.monthlyBudgets);
        } catch (e) {
          console.warn(`[transformBudgetData] Failed to parse monthlyBudgets for category ${cat.name}:`, e);
        }
      }
      
      return {
        id: parseInt(cat.id, 10),
        name: cat.name,
        type: cat.type as import('@/types/budget.types').CostCategoryType,
        budget: cat.budget,
        actual: cat.actual,
        committed: cat.committed,
        available: cat.available,
        progressPercent: cat.progressPercent,
        scope: cat.scope as 'global' | 'project' | undefined,
        globalCategoryId: cat.globalCategoryId ? parseInt(cat.globalCategoryId, 10) : undefined,
        isCustom: cat.isCustom,
        monthlyBudgets: monthlyBudgetsData,
      };
    }),
    phases: [], // Backend doesn't store phase breakdown yet
    totals: {
      budget: costBudget?.totalBudget ?? 0,
      actual: costBudget?.totalActual ?? 0,
      committed: costBudget?.totalCommitted ?? 0,
      available: costBudget?.totalAvailable ?? 0,
      progressPercent: costBudget?.progressPercent ?? 0,
    },
  };

  // Transform sales budget with billing milestones
  const transformedMilestones: import('@/types/budget.types').BillingMilestone[] = billingMilestones.map(m => ({
    id: typeof m.id === 'number' ? m.id : parseInt(String(m.id), 10),
    name: m.name,
    amount: m.amount,
    dueDate: m.dueDate ? m.dueDate.split('T')[0] : '',
    invoiced: m.invoiced,
    invoiceDate: m.invoiceDate,
    paid: m.paid,
    paidDate: m.paidDate,
    status: m.status as import('@/types/budget.types').BillingMilestoneStatus,
    // Default to 'date' until the backend returns triggerType — Phase 7 backend work
    triggerType: ((m as unknown as Record<string, unknown>).triggerType as import('@/types/budget.types').BillingMilestoneTriggerType) ?? 'date',
    triggerPercent: (m as unknown as Record<string, unknown>).triggerPercent as number | undefined,
  }));

  const transformedSalesBudget: import('@/types/budget.types').SalesBudget = {
    projectId,
    projectType,
    contractValue: salesBudget?.contractValue,
    invoiced: salesBudget?.invoiced,
    recognised: salesBudget?.recognised,
    remaining: salesBudget?.remaining,
    estimated: salesBudget?.estimated,
    actual: salesBudget?.actual,
    nteAmount: salesBudget?.nteAmount,
    unbilledTime: salesBudget?.unbilledTime,
    unbilledExpenses: salesBudget?.unbilledExpenses,
    estimatedRevenue: salesBudget?.estimatedRevenue,
    monthlyRetainer: salesBudget?.monthlyRetainer,
    includedHours: salesBudget?.includedHours,
    overageRate: salesBudget?.overageRate,
    milestones: transformedMilestones.length > 0 ? transformedMilestones : undefined,
    // Add required fields for SalesBudgetView to display the data
    // These are needed for the isDataIncomplete check in sales-budget-view.tsx
    revenueRecognition: salesBudget?.contractValue ? {
      method: 'milestone' as const,
      percentComplete: salesBudget?.contractValue ? ((salesBudget?.recognised ?? 0) / salesBudget.contractValue) * 100 : 0,
      revenueToRecognise: (salesBudget?.contractValue ?? 0) - (salesBudget?.recognised ?? 0),
      revenueRecognised: salesBudget?.recognised ?? 0,
      deferredRevenue: (salesBudget?.contractValue ?? 0) - (salesBudget?.recognised ?? 0),
      unbilledRevenue: (salesBudget?.unbilledTime ?? 0) + (salesBudget?.unbilledExpenses ?? 0),
    } : undefined,
    phases: transformedMilestones.length > 0 ? transformedMilestones.map((m) => {
      const isBilled = m.status === 'paid' || m.status === 'due';
      return {
        id: m.id,
        name: m.name,
        allocated: m.amount,
        recognised: isBilled ? m.amount : 0,
        invoiced: isBilled ? m.amount : 0,
        variance: m.amount - (isBilled ? m.amount : 0),
      };
    }) : undefined,
    categories: salesCategories.length > 0 ? (() => {
      const seenNames = new Set<string>();
      return salesCategories
        .map(cat => {
          let monthlyBudgetsData: Record<string, number> | undefined;
          if (cat.monthlyBudgets) {
            try {
              monthlyBudgetsData = JSON.parse(cat.monthlyBudgets);
            } catch (e) {
              console.warn(`[transformBudgetData] Failed to parse monthlyBudgets for sales category ${cat.name}:`, e);
            }
          }
          return {
            id: parseInt(cat.id, 10),
            name: cat.name,
            type: cat.type as import('@/types/budget.types').SalesCategoryType,
            budget: cat.budget,
            actual: cat.actual,
            remaining: cat.budget - cat.actual,
            percentBilled: cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0,
            scope: cat.scope as 'global' | 'project' | undefined,
            globalCategoryId: cat.globalCategoryId ? parseInt(cat.globalCategoryId, 10) : undefined,
            isCustom: cat.isCustom,
            monthlyBudgets: monthlyBudgetsData,
          };
        })
        .filter(cat => {
          if (seenNames.has(cat.name)) return false;
          seenNames.add(cat.name);
          return true;
        });
    })() : undefined,
    totals: salesCategories.length > 0 ? {
      budget: salesCategories.reduce((sum, cat) => sum + cat.budget, 0),
      actual: salesCategories.reduce((sum, cat) => sum + cat.actual, 0),
      remaining: salesCategories.reduce((sum, cat) => sum + (cat.budget - cat.actual), 0),
      percentBilled: (() => {
        const totalBudget = salesCategories.reduce((sum, cat) => sum + cat.budget, 0);
        const totalActual = salesCategories.reduce((sum, cat) => sum + cat.actual, 0);
        return totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;
      })(),
    } : undefined,
    retainer: salesBudget?.monthlyRetainer ? {
      monthlyAmount: salesBudget.monthlyRetainer,
      contractMonths: 12,
      totalContract: salesBudget.monthlyRetainer * 12,
      contractValue: salesBudget.monthlyRetainer * 12,
      invoiced: salesBudget?.invoiced ?? 0,
      remaining: (salesBudget.monthlyRetainer * 12) - (salesBudget?.invoiced ?? 0),
      monthsCompleted: 0,
      monthsRemaining: 12,
      currentMonth: {
        hoursIncluded: salesBudget.includedHours ?? 0,
        hoursUsed: 0,
        hoursRemaining: salesBudget.includedHours ?? 0,
        overageRate: salesBudget.overageRate ?? 0,
        progressPercent: 0,
      },
      rolloverPolicy: 'none' as const,
      monthlyBreakdown: [],
    } : undefined,
    utilisation: salesBudget?.monthlyRetainer ? {
      currentMonth: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
      includedHours: salesBudget.includedHours ?? 0,
      hoursUsed: 0,
      hoursRemaining: salesBudget.includedHours ?? 0,
      overageRate: salesBudget.overageRate ?? 0,
      percentUsed: 0,
      rolloverPolicy: 'none' as const,
    } : undefined,
  };

  // Transform resource budget with role, person, and phase breakdowns
  const transformedResourceBudget: import('@/types/budget.types').ResourceBudget = {
    id: resourceBudget?.id ? (typeof resourceBudget.id === 'number' ? resourceBudget.id : parseInt(String(resourceBudget.id), 10)) : undefined,
    projectId,
    summary: {
      totalBudget: resourceBudget?.totalBudget ?? 0,
      totalActual: resourceBudget?.totalActual ?? 0,
      totalRemaining: resourceBudget?.totalRemaining ?? 0,
      progressPercent: resourceBudget?.progressPercent ?? 0,
      billablePercent: resourceBudget?.billablePercent ?? 0,
      targetBillablePercent: resourceBudget?.targetBillablePercent ?? 85,
      totalBudgetCost: resourceBudget?.totalBudgetCost ?? 0,
      totalActualCost: resourceBudget?.totalActualCost ?? 0,
    },
    byRole: roleBudgets.map(rb => {
      // Parse monthlyHours JSON string from backend if present
      let monthlyHours: number[] | undefined;
      if (rb.monthlyHours) {
        try {
          monthlyHours = JSON.parse(rb.monthlyHours);
        } catch {
          monthlyHours = undefined;
        }
      }
      
      // If monthlyHours is not set but budgetHours has a value, distribute evenly across 12 months
      // This handles legacy data that was created before monthlyHours was added
      if (!monthlyHours && rb.budgetHours > 0) {
        const monthlyValue = Math.round((rb.budgetHours / 12) * 100) / 100; // Round to 2 decimal places
        const remainder = Math.round((rb.budgetHours - (monthlyValue * 12)) * 100) / 100;
        monthlyHours = Array(12).fill(monthlyValue);
        // Add any remainder to the last month to ensure total matches exactly
        if (remainder !== 0) {
          monthlyHours[11] = Math.round((monthlyHours[11] + remainder) * 100) / 100;
        }
      }
      
      return {
        id: typeof rb.id === 'number' ? rb.id : parseInt(String(rb.id), 10),
        roleId: rb.roleId ?? undefined,
        role: rb.roleName || '',
        budgetHours: rb.budgetHours,
        allocatedHours: rb.budgetHours, // Default to budget hours if not tracked separately
        actualHours: rb.actualHours,
        remainingHours: rb.remainingHours,
        percentUsed: rb.percentUsed,
        billRate: rb.billRate,
        budgetCost: rb.budgetCost,
        actualCost: rb.actualCost,
        monthlyHours,
      };
    }),
    byPerson: personBudgets.map(pb => ({
      id: typeof pb.id === 'number' ? pb.id : parseInt(String(pb.id), 10),
      personId: typeof pb.personId === 'number' ? pb.personId : parseInt(String(pb.personId), 10),
      personName: pb.personName,
      role: pb.roleName || '',
      budgetHours: pb.budgetHours,
      actualHours: pb.actualHours,
      utilisation: pb.utilisation,
      costRate: pb.costRate,
      budgetCost: pb.budgetCost,
      actualCost: pb.actualCost,
    })),
    byPhase: phaseBudgetResources.map(pbr => {
      const rawPhaseId = pbr.phaseId || pbr.id;
      return {
        phaseId: typeof rawPhaseId === 'number' ? rawPhaseId : parseInt(String(rawPhaseId), 10),
        phaseName: pbr.phaseName,
        budgetHours: pbr.budgetHours,
        actualHours: pbr.actualHours,
        remainingHours: pbr.remainingHours,
        status: pbr.status as 'not-started' | 'in-progress' | 'complete',
        startDate: pbr.startDate,
        endDate: pbr.endDate,
      };
    }),
  };

  // Transform alerts
  const transformedAlerts: import('@/types/budget.types').BudgetAlert[] = alerts.map(alert => ({
    id: typeof alert.id === 'number' ? alert.id : parseInt(String(alert.id), 10),
    projectId: typeof alert.projectId === 'number' ? alert.projectId : parseInt(String(alert.projectId), 10),
    type: alert.type as import('@/types/budget.types').BudgetAlertType,
    severity: alert.severity as import('@/types/budget.types').BudgetAlertSeverity,
    title: alert.title,
    description: alert.description,
    message: alert.message,
    details: alert.details,
    category: alert.category,
    actionLabel: alert.actionLabel,
    timestamp: alert.timestamp,
    createdAt: alert.createdAt,
    dismissed: alert.dismissed,
  }));

  // Transform revisions with computed changes field
  const transformedRevisions: import('@/types/budget.types').BudgetRevision[] = revisions.map(rev => ({
    id: typeof rev.id === 'number' ? rev.id : parseInt(String(rev.id), 10),
    projectId: typeof rev.projectId === 'number' ? rev.projectId : parseInt(String(rev.projectId), 10),
    revisionNumber: rev.revisionNumber,
    date: rev.date,
    createdAt: rev.createdAt,
    createdBy: rev.createdBy,
    changedBy: rev.changedBy,
    changedByName: rev.changedByName,
    reason: rev.reason,
    status: rev.status as import('@/types/budget.types').BudgetRevisionStatus,
    costBudget: rev.costBudget,
    salesBudget: rev.salesBudget,
    resourceBudget: rev.resourceBudget,
    costChange: rev.costChange,
    salesChange: rev.salesChange,
    resourceChange: rev.resourceChange,
    changes: {
      totalCostChange: rev.costChange ?? 0,
      totalRevenueChange: rev.salesChange ?? 0,
      categoriesChanged: [],
    },
    approvedBy: rev.approvedBy,
    approvedByName: rev.approvedByName,
    approvedDate: rev.approvedDate,
  }));

  // Create allBudgets view (profitability)
  const allBudgets: import('@/types/budget.types').AllBudgetsView = {
    projectId,
    profitability: {
      revenue: { budget: revenueBudget, actual: revenueActual, variance: revenueVariance },
      directCosts: { budget: costBudgetTotal, actual: costActual, variance: costVariance },
      grossProfit: { 
        budget: revenueBudget - costBudgetTotal, 
        actual: revenueActual - costActual, 
        variance: (revenueActual - costActual) - (revenueBudget - costBudgetTotal) 
      },
      grossMargin: { budget: budgetedMargin, actual: actualMargin, variance: actualMargin - budgetedMargin },
      overhead: { budget: 0, actual: 0, variance: 0 },
      netProfit: { 
        budget: revenueBudget - costBudgetTotal, 
        actual: revenueActual - costActual, 
        variance: (revenueActual - costActual) - (revenueBudget - costBudgetTotal) 
      },
      netMargin: { budget: budgetedMargin, actual: actualMargin, variance: actualMargin - budgetedMargin },
    },
    burnRateData: [], // Would need historical data
    forecast: {
      projectedFinalRevenue: revenueBudget,
      projectedFinalCost: costBudgetTotal,
      projectedFinalMargin: budgetedMargin,
      revenueVariance: 0,
      costVariance: 0,
      marginVariance: 0,
    },
  };

  return {
    projectId,
    projectName: `Project ${projectId}`,
    projectType,
    summary,
    costBudget: transformedCostBudget,
    salesBudget: transformedSalesBudget,
    resourceBudget: transformedResourceBudget,
    allBudgets,
    alerts: transformedAlerts,
    revisions: transformedRevisions,
    lastUpdated: new Date().toISOString(),
  };
}

// Fetch budget data for a project - routes based on data mode
export async function fetchBudgetData(projectId: number): Promise<ProjectBudgetData | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    return getMockBudgetData(projectId) || null;
  } else {
    // Live mode - call the individual budget API endpoints and compose the data
    const baseUrl = getApiBaseUrl();
    
    try {
      // Fetch all budget data in parallel using Promise.allSettled
      // This allows us to get partial data even if some endpoints fail
      const [costResult, salesResult, resourceResult, alertsResult, revisionsResult] = await Promise.allSettled([
        apiFetch<BackendCostBudget>(`${baseUrl}/CostBudgets/by-project/${projectId}`),
        apiFetch<BackendSalesBudget>(`${baseUrl}/SalesBudgets/by-project/${projectId}`),
        apiFetch<BackendResourceBudget>(`${baseUrl}/ResourceBudgets/by-project/${projectId}`),
        apiFetch<BackendBudgetAlert[]>(`${baseUrl}/BudgetAlerts/by-project/${projectId}`),
        apiFetch<BackendBudgetRevision[]>(`${baseUrl}/BudgetRevisions/by-project/${projectId}`),
      ]);

      // Extract values from settled promises, using null/empty for failures
      const costBudget = costResult.status === 'fulfilled' ? costResult.value : null;
      const salesBudget = salesResult.status === 'fulfilled' ? salesResult.value : null;
      const resourceBudget = resourceResult.status === 'fulfilled' ? resourceResult.value : null;
      const alerts = alertsResult.status === 'fulfilled' ? alertsResult.value : [];
      const revisions = revisionsResult.status === 'fulfilled' ? revisionsResult.value : [];

      // Log any failures for debugging
      if (costResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch cost budget for ${projectId}:`, costResult.reason);
      }
      if (salesResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch sales budget for ${projectId}:`, salesResult.reason);
      }
      if (resourceResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch resource budget for ${projectId}:`, resourceResult.reason);
      }

      // Fetch cost categories if we have a cost budget
      let costCategories: BackendCostCategory[] = [];

      if (costBudget?.id) {
        const costBudgetId = costBudget.id;
        const costCategoriesResult = await Promise.allSettled([
          apiFetch<BackendCostCategory[]>(`${baseUrl}/CostCategories/by-cost-budget/${costBudgetId}`),
        ]);

        costCategories = costCategoriesResult[0].status === 'fulfilled' ? costCategoriesResult[0].value : [];

        if (costCategoriesResult[0].status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch cost categories for cost budget ${costBudgetId}:`, costCategoriesResult[0].reason);
        }
      }

      // Fetch billing milestones and sales categories if we have a sales budget
      let billingMilestones: BackendBillingMilestone[] = [];
      let salesCategories: BackendSalesCategory[] = [];

      if (salesBudget?.id) {
        const salesBudgetId = salesBudget.id;
        const [billingMilestonesResult, salesCategoriesResult] = await Promise.allSettled([
          apiFetch<BackendBillingMilestone[]>(`${baseUrl}/BillingMilestones/by-sales-budget/${salesBudgetId}`),
          apiFetch<BackendSalesCategory[]>(`${baseUrl}/SalesCategories/by-sales-budget/${salesBudgetId}`),
        ]);

        billingMilestones = billingMilestonesResult.status === 'fulfilled' ? billingMilestonesResult.value : [];
        salesCategories = salesCategoriesResult.status === 'fulfilled' ? salesCategoriesResult.value : [];

        if (billingMilestonesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch billing milestones for sales budget ${salesBudgetId}:`, billingMilestonesResult.reason);
        }
        if (salesCategoriesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch sales categories for sales budget ${salesBudgetId}:`, salesCategoriesResult.reason);
        }
      }

      // Fetch role budgets, person budgets, and phase budget resources if we have a resource budget
      let roleBudgets: BackendRoleBudget[] = [];
      let personBudgets: BackendPersonBudget[] = [];
      let phaseBudgetResources: BackendPhaseBudgetResource[] = [];

      if (resourceBudget?.id) {
        const resourceBudgetId = resourceBudget.id;
        const [roleBudgetsResult, personBudgetsResult, phaseBudgetResourcesResult] = await Promise.allSettled([
          apiFetch<BackendRoleBudget[]>(`${baseUrl}/RoleBudgets/by-resource-budget/${resourceBudgetId}`),
          apiFetch<BackendPersonBudget[]>(`${baseUrl}/PersonBudgets/by-resource-budget/${resourceBudgetId}`),
          apiFetch<BackendPhaseBudgetResource[]>(`${baseUrl}/PhaseBudgetResources/by-resource-budget/${resourceBudgetId}`),
        ]);

        roleBudgets = roleBudgetsResult.status === 'fulfilled' ? roleBudgetsResult.value : [];
        personBudgets = personBudgetsResult.status === 'fulfilled' ? personBudgetsResult.value : [];
        phaseBudgetResources = phaseBudgetResourcesResult.status === 'fulfilled' ? phaseBudgetResourcesResult.value : [];

        // Log any failures for debugging
        if (roleBudgetsResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch role budgets for resource budget ${resourceBudgetId}:`, roleBudgetsResult.reason);
        }
        if (personBudgetsResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch person budgets for resource budget ${resourceBudgetId}:`, personBudgetsResult.reason);
        }
        if (phaseBudgetResourcesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch phase budget resources for resource budget ${resourceBudgetId}:`, phaseBudgetResourcesResult.reason);
        }
      }

      // Transform and return the composed budget data
      return transformBudgetData(projectId, costBudget, salesBudget, resourceBudget, alerts, revisions, roleBudgets, personBudgets, phaseBudgetResources, costCategories, billingMilestones, salesCategories);
    } catch (error) {
      console.warn(`[fetchBudgetData] Budget API not available for project ${projectId}:`, error);
      return null;
    }
  }
}

// Fetch the resource budget ID for a project (used as fallback when ID is not in local state)
export async function fetchResourceBudgetIdForProject(projectId: number): Promise<number | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const resourceBudget = await apiFetch<BackendResourceBudget>(`${baseUrl}/ResourceBudgets/by-project/${projectId}`);
    if (resourceBudget?.id) {
      return typeof resourceBudget.id === 'number'
        ? resourceBudget.id
        : parseInt(String(resourceBudget.id), 10);
    }
    return null;
  } catch {
    return null;
  }
}

// Create a PersonBudget record (team member allocation linked to a resource budget)
export interface CreatePersonBudgetData {
  resourceBudgetId: number;
  personId: number;
  personName: string;
  roleId?: number;
  budgetHours: number;
  costRate: number;
}

export async function createPersonBudget(data: CreatePersonBudgetData): Promise<BackendPersonBudget> {
  const baseUrl = getApiBaseUrl();
  const budgetCost = data.budgetHours * data.costRate;

  const payload = {
    resourceBudgetId: data.resourceBudgetId,
    personId: data.personId,
    personName: data.personName,
    roleId: data.roleId ?? null,
    budgetHours: data.budgetHours,
    actualHours: 0,
    utilisation: 0,
    costRate: data.costRate,
    budgetCost: budgetCost,
    actualCost: 0,
  };

  const created = await apiFetch<BackendPersonBudget>(`${baseUrl}/PersonBudgets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return created;
}

// Fetch category stats - computed from categories data
export async function fetchCategoryStats(): Promise<CategoryStats> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return mockCategoryStats;
  } else {
    // In live mode, fetch categories and compute stats
    const categories = await fetchGlobalCategories();
    
    const totalActive = categories.filter(c => c.status === 'active').length;
    const totalArchived = categories.filter(c => c.status === 'archived').length;
    
    // Sort by usage count and get top 5
    const mostUsedCategories = [...categories]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5)
      .map(c => ({ id: c.id, name: c.name, usageCount: c.usageCount }));
    
    return {
      totalGlobal: categories.length,
      totalProjectSpecific: 0, // Would need project-specific categories endpoint
      totalActive,
      totalArchived,
      mostUsedCategories,
    };
  }
}

// Column preferences - stored in UserPreferences table
export interface ColumnPreference {
  id: string;
  name: string;
  label?: string;
  description: string;
  category: string;
  visible: boolean;
  locked?: boolean;
  order: number;
  align?: 'left' | 'right';
}

/**
 * Fetch column preferences for the current authenticated user.
 * Uses the /userpreferences/me endpoint which identifies the user from the JWT token.
 * No userId parameter needed - the backend extracts it from the auth token.
 * 
 * @param pageKey - Unique key identifying which page's column preferences to retrieve
 *                  (e.g., 'projects-column-preferences', 'billing-invoice-column-preferences').
 *                  If not provided, returns all column preferences as a flat array (legacy behavior).
 */
export async function fetchColumnPreferences(pageKey?: string): Promise<ColumnPreference[] | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return null;
  } else {
    try {
      const response = await apiFetch<{ columnPreferences?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
      if (response?.columnPreferences) {
        const parsed = JSON.parse(response.columnPreferences);
        
        // If a pageKey is provided, extract that page's preferences from the keyed map
        if (pageKey) {
          // New format: { "pageKey": [...columns...], "pageKey2": [...columns...] }
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const pageColumns = parsed[pageKey];
            return Array.isArray(pageColumns) ? pageColumns as ColumnPreference[] : null;
          }
          // Legacy format: flat array - return as-is for backward compatibility
          if (Array.isArray(parsed)) {
            return parsed as ColumnPreference[];
          }
          return null;
        }
        
        // No pageKey: legacy behavior - return flat array
        if (Array.isArray(parsed)) {
          return parsed as ColumnPreference[];
        }
        return null;
      }
      return null;
    } catch {
      return null;
    }
  }
}

/**
 * Save column preferences for the current authenticated user.
 * Uses the PATCH /userpreferences/me/column-preferences endpoint which only updates
 * column preferences without overwriting other user preference fields.
 * No userId parameter needed - the backend extracts it from the auth token.
 * 
 * @param columns - The column preference array to save
 * @param pageKey - Unique key identifying which page's column preferences to save
 *                  (e.g., 'projects-column-preferences', 'billing-invoice-column-preferences').
 *                  When provided, preferences are stored in a keyed map so different pages
 *                  don't overwrite each other.
 */
export async function saveColumnPreferences(columns: ColumnPreference[], pageKey?: string): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return;
  } else {
    try {
      let columnPreferencesJson: string;
      
      if (pageKey) {
        // Keyed format: read existing preferences, merge in the new page's columns
        let allPreferences: Record<string, ColumnPreference[]> = {};
        try {
          const response = await apiFetch<{ columnPreferences?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
          if (response?.columnPreferences) {
            const parsed = JSON.parse(response.columnPreferences);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              allPreferences = parsed;
            }
          }
        } catch {
          // If we can't read existing preferences, start fresh
        }
        allPreferences[pageKey] = columns;
        columnPreferencesJson = JSON.stringify(allPreferences);
      } else {
        // Legacy format: store flat array
        columnPreferencesJson = JSON.stringify(columns);
      }
      
      await apiFetch(`${getApiBaseUrl()}/userpreferences/me/column-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          columnPreferences: columnPreferencesJson,
        }),
      });
    } catch (error) {
      console.error('Failed to save column preferences to API:', error);
      throw error;
    }
  }
}

// Project Creation Settings API functions
export interface ProjectCreationSettingsData {
  fieldConfigs: string | null;
  timelineSettings: string | null;
  fieldLengthConstraints: string | null;
}

export async function fetchProjectCreationSettings(): Promise<ProjectCreationSettingsData | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return null to use localStorage defaults
    return null;
  } else {
    try {
      const response = await apiFetch<{
        organisationId: string;
        fieldConfigs: string | null;
        timelineSettings: string | null;
        fieldLengthConstraints: string | null;
      }>(`${getApiBaseUrl()}/projectcreationsettings`);
      
      return {
        fieldConfigs: response.fieldConfigs,
        timelineSettings: response.timelineSettings,
        fieldLengthConstraints: response.fieldLengthConstraints,
      };
    } catch (error: unknown) {
      // Check if this is a 404 error - this is expected when the feature isn't deployed yet
      // In this case, silently fall back to localStorage defaults
      const appError = error as { metadata?: { apiError?: { status?: number } } };
      const statusCode = appError?.metadata?.apiError?.status;
      if (statusCode === 404) {
        // 404 is expected when the endpoint doesn't exist yet - use localStorage defaults silently
        return null;
      }
      // For other errors, log them as they may indicate real issues
      console.error('Failed to fetch project creation settings:', error);
      return null;
    }
  }
}

export async function saveProjectCreationSettings(data: ProjectCreationSettingsData): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, don't save to API (localStorage only)
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/projectcreationsettings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }
}

// ============================================================================
// PROJECT STATUS FUNCTIONS
// ============================================================================

export interface ProjectStatusOption {
  id: number;
  name: string;
}

// Default status labels used in demo/tour mode and as fallback
// Only includes the statuses available in the system's status picker
const DEFAULT_PROJECT_STATUSES: ProjectStatusOption[] = [
  { id: 1, name: 'Planning' },
  { id: 2, name: 'In Progress' },
  { id: 3, name: 'On Hold' },
  { id: 5, name: 'Cancelled' },
  { id: 6, name: 'Completed' },
];

export async function fetchProjectStatuses(): Promise<ProjectStatusOption[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return DEFAULT_PROJECT_STATUSES;
  } else {
    try {
      const statuses = await apiFetch<ProjectStatusOption[]>(`${getApiBaseUrl()}/projectstatuses`);
      return Array.isArray(statuses) ? statuses : DEFAULT_PROJECT_STATUSES;
    } catch (error: unknown) {
      const appError = error as { metadata?: { apiError?: { status?: number } } };
      const statusCode = appError?.metadata?.apiError?.status;
      if (statusCode === 404) {
        return DEFAULT_PROJECT_STATUSES;
      }
      console.error('Failed to fetch project statuses:', error);
      return DEFAULT_PROJECT_STATUSES;
    }
  }
}

export async function updateProjectStatus(id: number, name: string): Promise<void> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/projectstatuses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, name }),
    });
  }
}

// ============================================================================
// BUDGET SAVE FUNCTIONS
// ============================================================================

// Interface for saving billing milestone data
export interface SaveBillingMilestoneData {
  id?: number;
  name: string;
  amount: number;
  dueDate: string;
  invoiced?: boolean;
  invoiceDate?: string;
  paid?: boolean;
  paidDate?: string;
  status?: string;
}

// Interface for saving sales budget data
export interface SaveSalesCategoryData {
  id?: number;
  name: string;
  type: string;
  budget: number;
  actual?: number;
  committed?: number;
  available?: number;
  progressPercent?: number;
  scope?: string;
  globalCategoryId?: number;
  isCustom?: boolean;
  monthlyBudgets?: Record<string, number>;
}

export interface SaveSalesBudgetData {
  projectId: number;
  projectType: string;
  contractValue?: number;
  invoiced?: number;
  recognised?: number;
  remaining?: number;
  estimated?: number;
  actual?: number;
  nteAmount?: number;
  unbilledTime?: number;
  unbilledExpenses?: number;
  estimatedRevenue?: number;
  monthlyRetainer?: number;
  includedHours?: number;
  overageRate?: number;
  milestones?: SaveBillingMilestoneData[];
  categories?: SaveSalesCategoryData[];
}

// Helper function to save billing milestones for a sales budget
async function saveBillingMilestones(salesBudgetId: string, milestones: SaveBillingMilestoneData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing milestones for this sales budget
  let existingMilestones: BackendBillingMilestone[] = [];
  try {
    existingMilestones = await apiFetch<BackendBillingMilestone[]>(
      `${baseUrl}/BillingMilestones/by-sales-budget/${salesBudgetId}`
    );
  } catch (error) {
    // If 404, no existing milestones - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveBillingMilestones] Failed to fetch existing milestones:', error);
    }
  }
  
  // Track which existing milestones we've processed
  const processedIds = new Set<string>();
  
  // Save each milestone - update if exists, create if new
  for (const milestone of milestones) {
    const existing = milestone.id ? existingMilestones.find(m => String(m.id) === String(milestone.id)) : null;
    
    if (existing) {
      // Update existing milestone
      processedIds.add(String(existing.id));
      // Backend expects salesBudgetId as a number and dueDate as a valid date
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const defaultDueDate = milestone.dueDate || existing.dueDate || new Date().toISOString().split('T')[0];
      
      const updatePayload = {
        id: parseInt(existing.id, 10),
        salesBudgetId: numericSalesBudgetId,
        name: milestone.name,
        amount: milestone.amount,
        dueDate: defaultDueDate,
        invoiced: milestone.invoiced ?? existing.invoiced,
        invoiceDate: milestone.invoiceDate ?? existing.invoiceDate,
        paid: milestone.paid ?? existing.paid,
        paidDate: milestone.paidDate ?? existing.paidDate,
        status: milestone.status ?? existing.status,
      };
      
      await apiFetch<void>(`${baseUrl}/BillingMilestones/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new milestone
      // Backend expects salesBudgetId as a number and dueDate as a valid date
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const defaultDueDate = milestone.dueDate || new Date().toISOString().split('T')[0];
      
      const createPayload = {
        salesBudgetId: numericSalesBudgetId,
        name: milestone.name,
        amount: milestone.amount,
        dueDate: defaultDueDate,
        invoiced: milestone.invoiced ?? false,
        invoiceDate: milestone.invoiceDate ?? null,
        paid: milestone.paid ?? false,
        paidDate: milestone.paidDate ?? null,
        status: milestone.status ?? 'pending',
      };
      
      console.log('[saveBillingMilestones] Creating milestone with payload:', createPayload);
      
      await apiFetch<BackendBillingMilestone>(`${baseUrl}/BillingMilestones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
    }
  }
  
  // Delete milestones that were removed (not in the new list)
  for (const existing of existingMilestones) {
    if (!processedIds.has(String(existing.id)) && !milestones.some(m => String(m.id) === String(existing.id))) {
      await apiFetch<void>(`${baseUrl}/BillingMilestones/${existing.id}`, {
        method: 'DELETE',
      });
    }
  }
}

async function saveSalesCategories(salesBudgetId: string, categories: SaveSalesCategoryData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();

  let existingCategories: BackendSalesCategory[] = [];
  try {
    existingCategories = await apiFetch<BackendSalesCategory[]>(
      `${baseUrl}/SalesCategories/by-sales-budget/${salesBudgetId}`
    );
  } catch (error) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveSalesCategories] Failed to fetch existing categories:', error);
    }
  }

  const existingByName = new Map(existingCategories.map(c => [c.name, c]));
  const processedIds = new Set<string>();

  for (const category of categories) {
    const existing = category.id
      ? (existingCategories.find(c => c.id === String(category.id)) || existingByName.get(category.name))
      : existingByName.get(category.name);

    if (existing) {
      processedIds.add(String(existing.id));
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const updatePayload = {
        id: parseInt(existing.id, 10),
        salesBudgetId: numericSalesBudgetId,
        name: category.name,
        type: category.type,
        budget: category.budget,
        actual: category.actual ?? existing.actual,
        committed: category.committed ?? existing.committed,
        available: category.available ?? (category.budget - (category.actual ?? existing.actual) - (category.committed ?? existing.committed)),
        progressPercent: category.progressPercent ?? existing.progressPercent,
        scope: category.scope ?? existing.scope,
        globalCategoryId: category.globalCategoryId ?? (existing.globalCategoryId ? parseInt(existing.globalCategoryId, 10) : undefined),
        isCustom: category.isCustom ?? existing.isCustom,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : existing.monthlyBudgets,
      };

      await apiFetch<void>(`${baseUrl}/SalesCategories/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      const createPayload = {
        salesBudgetId: parseInt(salesBudgetId, 10),
        name: category.name,
        type: category.type,
        budget: category.budget,
        actual: category.actual ?? 0,
        committed: category.committed ?? 0,
        available: category.available ?? category.budget,
        progressPercent: category.progressPercent ?? 0,
        scope: category.scope,
        globalCategoryId: category.globalCategoryId,
        isCustom: category.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : null,
      };

      const created = await apiFetch<BackendSalesCategory>(`${baseUrl}/SalesCategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      processedIds.add(created.id);
    }
  }

  for (const existing of existingCategories) {
    if (!processedIds.has(existing.id) && !categories.some(c => c.id === parseInt(existing.id, 10))) {
      await apiFetch<void>(`${baseUrl}/SalesCategories/${existing.id}`, {
        method: 'DELETE',
      });
    }
  }
}

// Save sales budget - creates or updates based on whether one exists for the project
export async function saveSalesBudget(data: SaveSalesBudgetData): Promise<BackendSalesBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveSalesBudget] Demo mode - simulating save:', data);
    return {
      id: `demo-sales-budget-${Date.now()}`,
      ...data,
    };
  }
  
  // Live mode - check if a sales budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendSalesBudget>(
      `${getApiBaseUrl()}/salesbudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      projectType: data.projectType,
      contractValue: data.contractValue ?? existingBudget.contractValue,
      invoiced: data.invoiced ?? existingBudget.invoiced,
      recognised: data.recognised ?? existingBudget.recognised,
      remaining: data.remaining ?? existingBudget.remaining,
      estimated: data.estimated ?? existingBudget.estimated,
      actual: data.actual ?? existingBudget.actual,
      nteAmount: data.nteAmount ?? existingBudget.nteAmount,
      unbilledTime: data.unbilledTime ?? existingBudget.unbilledTime,
      unbilledExpenses: data.unbilledExpenses ?? existingBudget.unbilledExpenses,
      estimatedRevenue: data.estimatedRevenue ?? existingBudget.estimatedRevenue,
      monthlyRetainer: data.monthlyRetainer ?? existingBudget.monthlyRetainer,
      includedHours: data.includedHours ?? existingBudget.includedHours,
      overageRate: data.overageRate ?? existingBudget.overageRate,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/salesbudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updatePayload, id: parseInt(existingBudget.id, 10) }),
    });
    
    // Save milestones if provided
    if (data.milestones && data.milestones.length > 0) {
      await saveBillingMilestones(existingBudget.id, data.milestones);
    }

    // Save sales categories if provided
    if (data.categories && data.categories.length > 0) {
      await saveSalesCategories(existingBudget.id, data.categories);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new sales budget
      const createPayload = {
        projectId: data.projectId,
        projectType: data.projectType,
        contractValue: data.contractValue ?? 0,
        invoiced: data.invoiced ?? 0,
        recognised: data.recognised ?? 0,
        remaining: data.remaining ?? 0,
        estimated: data.estimated ?? 0,
        actual: data.actual ?? 0,
        nteAmount: data.nteAmount ?? 0,
        unbilledTime: data.unbilledTime ?? 0,
        unbilledExpenses: data.unbilledExpenses ?? 0,
        estimatedRevenue: data.estimatedRevenue ?? 0,
        monthlyRetainer: data.monthlyRetainer ?? 0,
        includedHours: data.includedHours ?? 0,
        overageRate: data.overageRate ?? 0,
      };
      
      const created = await apiFetch<BackendSalesBudget>(`${getApiBaseUrl()}/salesbudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save milestones if provided
      if (data.milestones && data.milestones.length > 0) {
        await saveBillingMilestones(created.id, data.milestones);
      }

      // Save sales categories if provided
      if (data.categories && data.categories.length > 0) {
        await saveSalesCategories(created.id, data.categories);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// Interface for saving cost category data
export interface SaveCostCategoryData {
  id?: number;
  name: string;
  type: string;
  budget: number;
  actual?: number;
  committed?: number;
  available?: number;
  progressPercent?: number;
  scope?: string;
  globalCategoryId?: number;
  isCustom?: boolean;
  monthlyBudgets?: Record<string, number>; // {"2024-12": 1000, "2025-01": 500, ...}
}

// Interface for saving cost budget data
export interface SaveCostBudgetData {
  projectId: number;
  totalBudget: number;
  totalActual?: number;
  totalCommitted?: number;
  totalAvailable?: number;
  progressPercent?: number;
  categories?: SaveCostCategoryData[];
}

// Helper function to save cost categories for a cost budget
async function saveCostCategories(costBudgetId: string, categories: SaveCostCategoryData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing categories for this cost budget
  let existingCategories: BackendCostCategory[] = [];
  try {
    existingCategories = await apiFetch<BackendCostCategory[]>(
      `${baseUrl}/CostCategories/by-cost-budget/${costBudgetId}`
    );
  } catch (error) {
    // If 404, no existing categories - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveCostCategories] Failed to fetch existing categories:', error);
    }
  }
  
  // Create a map of existing categories by name for easy lookup
  const existingByName = new Map(existingCategories.map(c => [c.name, c]));
  
  // Save each category - update if exists, create if new
  for (const category of categories) {
    const existing = category.id
      ? (existingCategories.find(c => c.id === String(category.id)) || existingByName.get(category.name))
      : existingByName.get(category.name);
    
    const numericCostBudgetId = typeof costBudgetId === 'string' ? parseInt(costBudgetId, 10) : costBudgetId;
    
    if (existing) {
      // Update existing category
      const actual = Number(category.actual ?? existing.actual) || 0;
      const committed = Number(category.committed ?? existing.committed) || 0;
      const budget = Number(category.budget) || 0;
      const globalCatId = category.globalCategoryId ?? existing.globalCategoryId;
      const updatePayload = {
        id: parseInt(existing.id, 10),
        costBudgetId: numericCostBudgetId,
        name: category.name,
        type: category.type,
        budget: budget,
        actual: actual,
        committed: committed,
        available: category.available != null ? Number(category.available) : (budget - actual - committed),
        progressPercent: Number(category.progressPercent ?? existing.progressPercent) || 0,
        scope: category.scope ?? existing.scope ?? null,
        globalCategoryId: globalCatId != null ? Number(globalCatId) : null,
        isCustom: category.isCustom ?? existing.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : (existing.monthlyBudgets ?? null),
      };
      
      await apiFetch<void>(`${baseUrl}/CostCategories/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new category
      const createPayload = {
        costBudgetId: numericCostBudgetId,
        name: category.name,
        type: category.type,
        budget: Number(category.budget) || 0,
        actual: Number(category.actual) || 0,
        committed: Number(category.committed) || 0,
        available: category.available != null ? Number(category.available) : (Number(category.budget) || 0),
        progressPercent: Number(category.progressPercent) || 0,
        scope: category.scope ?? null,
        globalCategoryId: category.globalCategoryId != null ? Number(category.globalCategoryId) : null,
        isCustom: category.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : null,
      };
      
      await apiFetch<BackendCostCategory>(`${baseUrl}/CostCategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
    }
  }
}

// Save cost budget - creates or updates based on whether one exists for the project
export async function saveCostBudget(data: SaveCostBudgetData): Promise<BackendCostBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveCostBudget] Demo mode - simulating save:', data);
    return {
      id: `demo-cost-budget-${Date.now()}`,
      projectId: data.projectId,
      totalBudget: data.totalBudget,
      totalActual: data.totalActual ?? 0,
      totalCommitted: data.totalCommitted ?? 0,
      totalAvailable: data.totalAvailable ?? data.totalBudget,
      progressPercent: data.progressPercent ?? 0,
    };
  }
  
  // Live mode - check if a cost budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendCostBudget>(
      `${getApiBaseUrl()}/costbudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      totalBudget: data.totalBudget,
      totalActual: data.totalActual ?? existingBudget.totalActual,
      totalCommitted: data.totalCommitted ?? existingBudget.totalCommitted,
      totalAvailable: data.totalAvailable ?? (data.totalBudget - (data.totalActual ?? existingBudget.totalActual) - (data.totalCommitted ?? existingBudget.totalCommitted)),
      progressPercent: data.progressPercent ?? existingBudget.progressPercent,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/costbudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updatePayload, id: parseInt(existingBudget.id, 10) }),
    });
    
    // Save categories if provided
    if (data.categories && data.categories.length > 0) {
      await saveCostCategories(existingBudget.id, data.categories);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new cost budget
      const createPayload = {
        projectId: data.projectId,
        totalBudget: data.totalBudget,
        totalActual: data.totalActual ?? 0,
        totalCommitted: data.totalCommitted ?? 0,
        totalAvailable: data.totalAvailable ?? data.totalBudget,
        progressPercent: data.progressPercent ?? 0,
      };
      
      const created = await apiFetch<BackendCostBudget>(`${getApiBaseUrl()}/costbudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save categories if provided
      if (data.categories && data.categories.length > 0) {
        await saveCostCategories(created.id, data.categories);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// Interface for saving role budget data
export interface SaveRoleBudgetData {
  id?: number;
  roleId?: number;
  role: string;
  budgetHours: number;
  actualHours?: number;
  remainingHours?: number;
  percentUsed?: number;
  billRate: number;
  budgetCost?: number;
  actualCost?: number;
  monthlyHours?: number[];
}

// Interface for saving resource budget data
export interface SaveResourceBudgetData {
  projectId: number;
  totalBudget?: number;
  totalActual?: number;
  totalRemaining?: number;
  progressPercent?: number;
  billablePercent?: number;
  targetBillablePercent?: number;
  totalBudgetCost?: number;
  totalActualCost?: number;
  byRole?: SaveRoleBudgetData[];
}

// Helper function to save role budgets for a resource budget
async function saveRoleBudgets(resourceBudgetId: string, roles: SaveRoleBudgetData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing role budgets for this resource budget
  let existingRoles: BackendRoleBudget[] = [];
  try {
    existingRoles = await apiFetch<BackendRoleBudget[]>(
      `${baseUrl}/RoleBudgets/by-resource-budget/${resourceBudgetId}`
    );
  } catch (error) {
    // If 404, no existing roles - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveRoleBudgets] Failed to fetch existing roles:', error);
    }
  }
  
  // Create a map of existing roles by role name for easy lookup
  const existingByRole = new Map(existingRoles.map(r => [r.roleId, r]));
  
  // Track which existing roles we've processed (to delete ones not in the new list)
  const processedRoleIds = new Set<string>();
  
  // Save each role - update if exists, create if new
  for (const role of roles) {
    const existing = role.id ? existingRoles.find(r => r.id === String(role.id)) : (role.roleId ? existingByRole.get(role.roleId) : undefined);
    
    // Calculate derived values
    const budgetHours = role.budgetHours;
    const actualHours = role.actualHours ?? 0;
    const remainingHours = role.remainingHours ?? (budgetHours - actualHours);
    const percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
    const budgetCost = role.budgetCost ?? (budgetHours * role.billRate);
    const actualCost = role.actualCost ?? (actualHours * role.billRate);
    
    // Serialize monthlyHours to JSON string for backend
    const monthlyHoursJson = role.monthlyHours ? JSON.stringify(role.monthlyHours) : null;
    
    if (existing) {
      // Update existing role
      processedRoleIds.add(existing.id);
      
      const updatePayload = {
        id: existing.id,
        resourceBudgetId,
        roleId: role.roleId ?? null,
        budgetHours,
        actualHours,
        remainingHours,
        percentUsed,
        billRate: role.billRate,
        budgetCost,
        actualCost,
        monthlyHours: monthlyHoursJson,
      };
      
      await apiFetch<void>(`${baseUrl}/RoleBudgets/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new role
      const createPayload = {
        resourceBudgetId,
        roleId: role.roleId ?? null,
        budgetHours,
        actualHours,
        remainingHours,
        percentUsed,
        billRate: role.billRate,
        budgetCost,
        actualCost,
        monthlyHours: monthlyHoursJson,
      };
      
      const created = await apiFetch<BackendRoleBudget>(`${baseUrl}/RoleBudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      processedRoleIds.add(created.id);
    }
  }
  
  // Delete any existing roles that are no longer in the list
  for (const existingRole of existingRoles) {
    if (!processedRoleIds.has(existingRole.id)) {
      try {
        await apiFetch<void>(`${baseUrl}/RoleBudgets/${existingRole.id}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.warn(`[saveRoleBudgets] Failed to delete role ${existingRole.id}:`, error);
      }
    }
  }
}

// Save resource budget - creates or updates based on whether one exists for the project
export async function saveResourceBudget(data: SaveResourceBudgetData): Promise<BackendResourceBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveResourceBudget] Demo mode - simulating save:', data);
    return {
      id: `demo-resource-budget-${Date.now()}`,
      projectId: data.projectId,
      totalBudget: data.totalBudget ?? 0,
      totalActual: data.totalActual ?? 0,
      totalRemaining: data.totalRemaining ?? 0,
      progressPercent: data.progressPercent ?? 0,
      billablePercent: data.billablePercent ?? 0,
      targetBillablePercent: data.targetBillablePercent ?? 85,
      totalBudgetCost: data.totalBudgetCost ?? 0,
      totalActualCost: data.totalActualCost ?? 0,
    };
  }
  
  // Calculate totals from role budgets if provided
  let totalBudget = data.totalBudget ?? 0;
  let totalBudgetCost = data.totalBudgetCost ?? 0;
  
  if (data.byRole && data.byRole.length > 0) {
    totalBudget = data.byRole.reduce((sum, role) => sum + role.budgetHours, 0);
    totalBudgetCost = data.byRole.reduce((sum, role) => sum + (role.budgetCost ?? role.budgetHours * role.billRate), 0);
  }
  
  // Live mode - check if a resource budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendResourceBudget>(
      `${getApiBaseUrl()}/resourcebudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      totalBudget,
      totalActual: data.totalActual ?? existingBudget.totalActual,
      totalRemaining: data.totalRemaining ?? (totalBudget - (data.totalActual ?? existingBudget.totalActual)),
      progressPercent: data.progressPercent ?? existingBudget.progressPercent,
      billablePercent: data.billablePercent ?? existingBudget.billablePercent,
      targetBillablePercent: data.targetBillablePercent ?? existingBudget.targetBillablePercent,
      totalBudgetCost,
      totalActualCost: data.totalActualCost ?? existingBudget.totalActualCost,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/resourcebudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePayload),
    });
    
    // Save role budgets if provided
    if (data.byRole && data.byRole.length > 0) {
      await saveRoleBudgets(existingBudget.id, data.byRole);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new resource budget
      const createPayload = {
        projectId: data.projectId,
        totalBudget,
        totalActual: data.totalActual ?? 0,
        totalRemaining: data.totalRemaining ?? totalBudget,
        progressPercent: data.progressPercent ?? 0,
        billablePercent: data.billablePercent ?? 0,
        targetBillablePercent: data.targetBillablePercent ?? 85,
        totalBudgetCost,
        totalActualCost: data.totalActualCost ?? 0,
      };
      
      const created = await apiFetch<BackendResourceBudget>(`${getApiBaseUrl()}/resourcebudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save role budgets if provided
      if (data.byRole && data.byRole.length > 0) {
        await saveRoleBudgets(created.id, data.byRole);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// ============================================================
// Billing API Functions
// ============================================================

export interface BillingWipItem {
  id: number;
  projectId: number;
  sourceType: number; // 1=Timesheet, 2=Expense, 3=Disbursement
  sourceId: number;
  taskId?: number;
  employeeId?: number;
  date: string;
  description: string;
  originalQuantity: number;
  originalRate: number;
  originalAmount: number;
  quantity: number;
  unit: string;
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  costRate?: number;
  status: number; // 0=Unbilled, 1=Draft, 2=Invoiced, 3=WriteOff, 4=Void
  isWriteOff: boolean;
  invoiceId?: number;
  billedDate?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
  employeeName?: string;
  taskName?: string;
  externalCostCentreCode?: string;
}

export interface BillingInvoice {
  id: number;
  projectId: number;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  description?: string;
  notes?: string;
  status: number; // 0=Draft, 1=Sent, 2=PartiallyPaid, 3=Paid, 4=Overdue, 5=Void, 6=WriteOff
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  grossTotal: number;
  accessFinancialsId?: string;
  sentDate?: string;
  paidDate?: string;
  paidAmount?: number;
  voidDate?: string;
  voidReason?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
}

export interface BillingInvoiceLineItem {
  id: number;
  invoiceId: number;
  wipLedgerId?: number;
  sortOrder: number;
  description: string;
  quantity: number;
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  financialsTransactionId?: number;
  createdBy?: string;
  updatedBy?: string;
}

const INVOICE_STATUS_MAP: Record<number, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'PartiallyPaid',
  3: 'Paid',
  4: 'Overdue',
  5: 'Void',
  6: 'WriteOff',
};

const WIP_SOURCE_TYPE_MAP: Record<number, string> = {
  1: 'Time',
  2: 'Expense',
  3: 'Purchase',
  4: 'Invoice',
  5: 'Credit Note',
  6: 'Adjustment',
  7: 'Journal',
  8: 'Sales',
  9: 'Miscellaneous',
  99: 'Other',
};

export function mapInvoiceStatus(status: number): string {
  return INVOICE_STATUS_MAP[status] || 'Unknown';
}

export function mapWipSourceType(sourceType: number): string {
  return WIP_SOURCE_TYPE_MAP[sourceType] || 'Unknown';
}

export interface WipSummary {
  totalNetAmount: number;
  timeNetAmount: number;
  expenseNetAmount: number;
  timeHours: number;
}

export interface PaginatedBillingWipResponse {
  items: BillingWipItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  summary?: WipSummary;
}

export async function fetchBillingWipItems(projectId: number, status?: number, page?: number, pageSize?: number): Promise<BillingWipItem[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Demo mode returns empty array - mock data is handled in the component
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingWipItem[]>(`${getApiBaseUrl()}/billing/${projectId}/wip${statusParam}`);
  }
}

export async function fetchBillingWipItemsPaged(projectId: number, status: number, page: number, pageSize: number): Promise<PaginatedBillingWipResponse> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize, summary: { totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 } };
  } else {
    return apiFetch<PaginatedBillingWipResponse>(
      `${getApiBaseUrl()}/billing/${projectId}/wip?status=${status}&page=${page}&pageSize=${pageSize}`
    );
  }
}

export async function fetchBillingInvoices(projectId: number, status?: number): Promise<BillingInvoice[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingInvoice[]>(`${getApiBaseUrl()}/billing/${projectId}/invoices${statusParam}`);
  }
}

export async function fetchBillingInvoiceById(invoiceId: number): Promise<{ invoice: BillingInvoice; lineItems: BillingInvoiceLineItem[] }> {
  return apiFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceId}`);
}

export interface CustomerTermsResult {
  dueDays: number | null;
  dueType?: string;
  code?: string;
  description?: string;
  error?: string;
}

export async function getCustomerTerms(customerCode: string, companyId: number): Promise<CustomerTermsResult> {
  return apiFetch<CustomerTermsResult>(`${getApiBaseUrl()}/billing/customer-terms?customerCode=${encodeURIComponent(customerCode)}&companyId=${companyId}`);
}

export async function createBillingDraft(projectId: number, wipItemIds: number[], description?: string, notes?: string, invoiceDate?: string, dueDate?: string): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/${projectId}/drafts`, {
    method: 'POST',
    body: JSON.stringify({ wipItemIds, description, notes, invoiceDate, dueDate }),
  });
}

export interface UpdateDraftRequest {
  additions?: number[];
  removals?: number[];
  edits?: Array<{
    wipLedgerId: number;
    quantity?: number;
    rate?: number;
    netAmount?: number;
    description?: string;
  }>;
}

export async function updateBillingDraft(invoiceId: number, updates: UpdateDraftRequest): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function updateDraftDescription(invoiceId: number, description: string | null): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/description`, {
    method: 'PATCH',
    body: JSON.stringify({ description }),
  });
}

export async function deleteBillingDraft(invoiceId: number): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'DELETE',
  });
}

export interface PostToFinancialsResponse {
  success: boolean;
  message?: string;
  financialsIds?: number[];
  error?: string;
  validationErrors?: Array<{
    message?: string;
    details?: Array<{ primaryKey?: string; field?: string; value?: string }>;
  }>;
}

export async function postBillingDraftToFinancials(invoiceId: number): Promise<PostToFinancialsResponse> {
  console.log(`[Billing] Posting draft invoice ${invoiceId} to Financials...`);

  const response = await authFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/post`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  console.log(`[Billing] Response status: ${response.status}`, data);

  if (response.ok) {
    // 200 OK — success
    console.log(`[Billing] Success — Financials IDs:`, data.financialsIds);
    return {
      success: true,
      message: data.message,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 422) {
    // Validation errors from Financials API
    console.warn(`[Billing] Validation errors for invoice ${invoiceId}:`, data.validationErrors);
    return {
      success: false,
      error: data.error,
      validationErrors: data.validationErrors,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 400) {
    // Bad request (e.g. invoice not a draft)
    console.warn(`[Billing] Bad request for invoice ${invoiceId}:`, data.error);
    return {
      success: false,
      error: data.error || 'Invalid request',
    };
  }

  // 502 / 500 / other errors
  console.error(`[Billing] Error posting invoice ${invoiceId} — status ${response.status}:`, data);
  return {
    success: false,
    error: data.error || data.message || `Request failed with status ${response.status}`,
    financialsIds: data.financialsIds,
  };
}

export async function voidBillingInvoice(invoiceId: number, reason: string): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ── Project Transactions ───────────────────────────────────────────────

export interface ProjectTransactionQueryParams {
  first?: number;
  after?: string | null;
  projectCode?: string;
  entryType?: string;
  isBatched?: boolean | null;
  isInvoiced?: boolean | null;
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortOrder?: 'Ascending' | 'Descending';
}

export interface ProjectTransactionPageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
}

export interface ProjectTransactionDto {
  transactionId: number | null;
  projectCode: string | null;
  projectName: string | null;
  projectStatus: string | null;
  date: string | null;
  periodNumber: number | null;
  yearLabel: string | null;
  weekNo: number | null;
  dateEntered: string | null;
  transactionType: string | null;
  transactionStatus: string | null;
  source: string | null;
  entryType: string | null;
  isBatched: boolean | null;
  isInvoiced: boolean | null;
  reference: string | null;
  internalRef: number | null;
  detail: string | null;
  batchRef: string | null;
  sortKey: string | null;
  headerKey: string | null;
  resourceCode: string | null;
  resourceName: string | null;
  resourcePriceLine: number | null;
  employeeCode: string | null;
  enteredBy: string | null;
  accountCode: string | null;
  accountName: string | null;
  invoiceAccountCode: string | null;
  invoiceAccountName: string | null;
  orderAccountCode: string | null;
  orderAccountName: string | null;
  supplierAccountCode: string | null;
  costCentreCode: string | null;
  costCentreName: string | null;
  quantity: number | null;
  price: number | null;
  value: number | null;
  uplift: number | null;
  upliftValue: number | null;
  priceWithUplift: number | null;
  currencyCode: string | null;
  currentPrice: number | null;
  currentValue: number | null;
  currentUpliftValue: number | null;
  actualSales: number | null;
  actualCost: number | null;
  salesOrderNumber: number | null;
  purchaseOrderNumber: string | null;
  stockCode: string | null;
  stockName: string | null;
  analysisCode: string | null;
  analysisName: string | null;
  nominalCode: string | null;
  payElement: string | null;
  payRate: number | null;
  subLedger: string | null;
  customField1: string | null;
  customField2: string | null;
  customField3: string | null;
  customField4: string | null;
  customField5: string | null;
  customField6: string | null;
  userKey1: string | null;
  userKey2: string | null;
  userKey3: string | null;
  userChar1: string | null;
  userChar2: string | null;
  userChar3: string | null;
  userChar4: string | null;
  userDate1: string | null;
  userDate2: string | null;
  userFlag1: boolean | null;
  userFlag2: boolean | null;
  userNumber1: number | null;
  userNumber2: number | null;
}

export interface ProjectTransactionResponse {
  items: ProjectTransactionDto[];
  pageInfo: ProjectTransactionPageInfo | null;
  errorMessage: string | null;
}

/**
 * Fetches project transactions from the backend.
 * In demo mode returns mock data; in live mode queries the real API
 * which delegates to the external transaction data provider (e.g. Financials GraphQL).
 */
export async function fetchProjectTransactions(
  params: ProjectTransactionQueryParams = {}
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    // Return empty result for demo mode — the page already has its own mock data
    return { items: [], pageInfo: null, errorMessage: null };
  }

  // Build query string — companyId is sent via the X-Company-Id header by authFetch
  const queryParams = new URLSearchParams();
  if (params.first) queryParams.set('first', params.first.toString());
  if (params.after) queryParams.set('after', params.after);
  if (params.projectCode) queryParams.set('projectCode', params.projectCode);
  if (params.entryType) queryParams.set('entryType', params.entryType);
  if (params.isBatched != null) queryParams.set('isBatched', params.isBatched.toString());
  if (params.isInvoiced != null) queryParams.set('isInvoiced', params.isInvoiced.toString());
  if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params.sortField) queryParams.set('sortField', params.sortField);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions?${queryParams.toString()}`
  );
}

/**
 * Request body for the POST /api/projecttransactions/query endpoint.
 * Supports multi-field sorting and structured filters for server-side
 * pagination, filtering, and sorting.
 */
export interface ProjectTransactionQueryBody {
  companyId?: string;
  first: number;
  after?: string | null;
  sort?: Array<{ field: string; order: string }>;
  projectCode?: string;
  projectCodes?: string[];
  transactionTypes?: string[];
  sources?: string[];
  entryType?: string;
  isBatched?: boolean;
  isInvoiced?: boolean;
  projectStatuses?: string[];
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Queries project transactions using the POST /query endpoint.
 * Supports multi-field sorting and advanced filters via a JSON body,
 * enabling true server-side pagination, filtering, and sorting.
 */
export async function queryProjectTransactions(
  body: ProjectTransactionQueryBody
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    return { items: [], pageInfo: null, errorMessage: null };
  }

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions/query`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

// ── Sales Transactions ─────────────────────────────────────────────────

export interface BulkCreateSalesTransactionRequest {
  transactions: Array<Record<string, unknown>>;
  validateOnly?: boolean;
}

export interface BulkCreateSalesTransactionResponse {
  ids?: number[];
  errors?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  warnings?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  errorMessage?: string;
}

export async function bulkCreateSalesTransactions(
  request: BulkCreateSalesTransactionRequest,
  companyId: number
): Promise<BulkCreateSalesTransactionResponse> {
  return apiFetch<BulkCreateSalesTransactionResponse>(
    `${getApiBaseUrl()}/billing/sales-transactions/bulk-create?companyId=${companyId}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}
